import type { NdtGlobalListResult } from "../../domain/ndt/ndt-global-list-query.js";
import { documentEngine } from "../../infrastructure/documents/document-engine.js";
import { buildNdtAssetsXlsxExport } from "../../domain/ndt/ndt-equipment-import.js";
import type { NdtAssetListPage } from "../../domain/ndt/ndt-asset-list-query.js";
import { renderNdtDashboard } from "./ndt-registry-dashboard.js";
import type { NdtAsset, NdtAssetType } from "../../domain/ndt/ndt-assets.js";
import type { NdtAssetCertificate, NdtCertificateAttachment, NdtCertificateStatus } from "../../domain/ndt/ndt-asset-history.js";
import { CALIBRATION_STATUS_LABELS, resolveCalibrationStatus } from "../../domain/ndt/ndt-calibration.js";
import {
  TRANSDUCER_TYPES,
  TRANSDUCER_TYPE_OTHER,
  TRANSDUCER_FREQUENCIES_MHZ,
  TRANSDUCER_FREQUENCY_OTHER,
  TRANSDUCER_DIMENSION_UNITS,
  TRANSDUCER_DEFAULT_APPLICATION,
  formatTransducerFrequency,
  formatTransducerDimension,
  isOtherTransducerType,
  isStandardFrequency
} from "../../domain/ndt/ndt-transducer.js";
import { contextStore } from "../auth/context-store.js";
import { PermissionCode } from "../../permissions/permissions.js";
import {
  ME_MEASUREMENT_TECHNIQUES,
  NDT_METHODS,
  NDT_METHOD_LABELS,
  type NdtMethod,
  type NdtProcedure,
  type NdtProcedureAttachment,
  type NdtProcedureFilters
} from "../../domain/ndt/ndt-procedure.js";
import { METHOD_A_COEFFICIENT_K, METHOD_A_REFERENCE_TEMPERATURE_C } from "../../domain/engineering/me-thermal-correction.js";
import {
  NDT_CATALOG_KEYS,
  NDT_CATALOG_LABELS,
  NDT_CATALOG_SINGULAR,
  type NdtCatalogItem,
  type NdtCatalogKey
} from "../../domain/ndt/ndt-technical-catalog.js";
import {
  LP_CONSUMABLE_TYPES,
  LP_CONSUMABLE_TYPE_LABELS,
  formatCivilDateBR,
  isLpConsumableExpired,
  type LpConsumable,
  type LpConsumableType
} from "../../domain/ndt/lp-consumable.js";
import {
  PM_CONSUMABLE_TYPES,
  PM_TECHNIQUES,
  formatPmConsumableType,
  formatPmTechnique,
  isPmConsumableExpired,
  type PmConsumable
} from "../../domain/ndt/pm-consumable.js";
import {
  buildNdtEquipmentXlsxTemplate,
  buildNdtEquipmentCsvTemplate,
  NDT_IMPORT_TEMPLATE_SHEETS,
  type NdtImportStagedItem,
  type NdtImportSummary,
  type NdtImportExecutionResult
} from "../../domain/ndt/ndt-equipment-import.js";

const DASHBOARD_TAB = "DASHBOARD" as const;
const PROCEDURES_TAB = "PROCEDURES" as const;
/** Cadastro global END → Consumíveis LP (penetrante, removedor, revelador). */
const LP_CONSUMABLES_TAB = "LP_CONSUMABLES" as const;
/** Cadastro global END → Consumíveis PM — catálogo próprio, nunca misturado ao LP. */
const PM_CONSUMABLES_TAB = "PM_CONSUMABLES" as const;
type RegistryTab = NdtAssetType | typeof DASHBOARD_TAB | typeof PROCEDURES_TAB | NdtCatalogKey | typeof LP_CONSUMABLES_TAB | typeof PM_CONSUMABLES_TAB;

function isCatalogTab(tab: RegistryTab): tab is NdtCatalogKey {
  return (NDT_CATALOG_KEYS as readonly string[]).includes(tab);
}

export class NdtRegistryPage extends HTMLElement {
  private assets: Record<NdtAssetType, NdtAsset[]> = {
    'ULTRASONIC_THICKNESS_GAUGE': [],
    'ULTRASONIC_TRANSDUCER': [],
    'MAGNETIC_YOKE': [],
    'BSCAN_EQUIPMENT': [],
    'CALIBRATION_BLOCK': [],
    'PYROMETER': []
  };
  
  private activeTab: RegistryTab = DASHBOARD_TAB;
  /** M21 — agrupamento sem sacrificar os formulários operacionais existentes. */
  private activeGroup: 'ASSETS' | 'DOCUMENTATION' | 'CONSUMABLES' = 'ASSETS';
  private currentContext: any;
  private p: any; // platform

  private procedures: NdtProcedure[] = [];
  private procedureFilters: NdtProcedureFilters = {};
  private procedureError: string | null = null;
  private catalogItems: NdtCatalogItem[] = [];
  private lpConsumables: LpConsumable[] = [];
  private lpConsumableTypeFilter: LpConsumableType | "" = "";
  private pmConsumables: PmConsumable[] = [];
  private pmConsumableTypeFilter = "";
  private pmConsumableSearch = "";
  /** Latest calibration certificate per asset, for the listing columns and the status filter. */
  private certificatesByAsset = new Map<string, any | null>();
  private calibrationFilter: "" | NdtCertificateStatus = "";
  private assetsLoadError = false;
  private assetSearch = "";
  private assetPage: NdtAssetListPage | null = null;
  private assetPageCursor: string | null = null;
  private assetPreviousCursors: Array<string | null> = [];
  private assetPageError: string | null = null;
  private assetPageRequest = 0;
  private globalPage: NdtGlobalListResult<any> | null = null;
  private globalPageTab: RegistryTab | null = null;
  private globalSearch = "";
  private globalCursor: string | null = null;
  private globalPreviousCursors: Array<string | null> = [];
  private globalError: string | null = null;
  private globalRequest = 0;

  connectedCallback() {
    const requested = new URL(window.location.href).searchParams.get('aba');
    if (requested && (requested === DASHBOARD_TAB || (
      ['ULTRASONIC_THICKNESS_GAUGE', 'ULTRASONIC_TRANSDUCER', 'MAGNETIC_YOKE', 'BSCAN_EQUIPMENT',
       'CALIBRATION_BLOCK', 'PYROMETER', PROCEDURES_TAB, LP_CONSUMABLES_TAB, PM_CONSUMABLES_TAB, ...NDT_CATALOG_KEYS] as readonly string[]
    ).includes(requested))) {
      this.activeTab = requested as RegistryTab;
      this.activeGroup = requested === DASHBOARD_TAB ? 'ASSETS'
        : (['ULTRASONIC_THICKNESS_GAUGE', 'ULTRASONIC_TRANSDUCER', 'MAGNETIC_YOKE', 'BSCAN_EQUIPMENT', 'CALIBRATION_BLOCK', 'PYROMETER'].includes(requested) ? 'ASSETS'
          : requested === LP_CONSUMABLES_TAB || requested === PM_CONSUMABLES_TAB ? 'CONSUMABLES' : 'DOCUMENTATION');
    }
    this.p = (window as any).app.platform;
    this.currentContext = { activeClientId: contextStore.get().activeClientId, userId: contextStore.get().userId, userRole: contextStore.get().role, permissions: contextStore.get().permissions };
    this.loadAssets();
  }

  private async loadAssets() {
    this.assetsLoadError = false;
    try {
      const allAssets = await this.p.ndtAssetService.listAssets(this.currentContext, undefined, true);
      
      this.assets = {
        'ULTRASONIC_THICKNESS_GAUGE': [],
        'ULTRASONIC_TRANSDUCER': [],
        'MAGNETIC_YOKE': [],
        'BSCAN_EQUIPMENT': [],
        'CALIBRATION_BLOCK': [],
    'PYROMETER': []
      };
      
      for (const a of allAssets) {
        if (this.assets[a.assetType as NdtAssetType]) {
          this.assets[a.assetType as NdtAssetType].push(a);
        }
      }

      await this.loadCertificates([
        ...this.assets['ULTRASONIC_THICKNESS_GAUGE'],
        ...this.assets['CALIBRATION_BLOCK'],
        ...this.assets['MAGNETIC_YOKE'],
        ...this.assets['PYROMETER']
      ]);
    } catch (e) {
      console.error(e);
      this.assetsLoadError = true;
    }
    await this.loadProcedures();
    await this.loadCatalogItems();
    await this.loadLpConsumables();
    await this.loadPmConsumables();
    this.render();
    if (this.isAssetTab(this.activeTab)) void this.refreshAssetPage();
    else if (this.isGlobalTab(this.activeTab)) void this.refreshGlobalPage();
  }

  private async loadPmConsumables() {
    try {
      this.pmConsumables = await this.p.pmConsumableService.list(this.currentContext);
    } catch (e) {
      console.error("[END CONSUMÍVEIS PM] Falha ao carregar consumíveis", e);
      this.pmConsumables = [];
    }
  }

  private async loadLpConsumables() {
    try {
      this.lpConsumables = await this.p.lpConsumableService.list(this.currentContext);
    } catch (e) {
      console.error("[END CONSUMÍVEIS LP] Falha ao carregar consumíveis", e);
      this.lpConsumables = [];
    }
  }

  private async loadCatalogItems() {
    try {
      this.catalogItems = await this.p.ndtCatalogService.list(this.currentContext);
    } catch (e) {
      console.error("[END CATÁLOGOS] Falha ao carregar catálogos técnicos", e);
      this.catalogItems = [];
    }
  }

  private async loadProcedures() {
    try {
      this.procedures = await this.p.ndtProcedureService.list(this.currentContext, this.procedureFilters);
      this.procedureError = null;
    } catch (e: any) {
      console.error("[END PROCEDIMENTOS] Falha ao carregar procedimentos", e);
      this.procedures = [];
      this.procedureError = e?.safeMessage || e?.message || "Não foi possível carregar os procedimentos de END.";
    }
  }

  /** Reúne apenas dados autorizados; preserva filtros, sem limitar-se à página atual. */
  private async collectAssetsForExport(maxRows: number): Promise<NdtAsset[]> {
    if (!this.isAssetTab(this.activeTab)) return [];
    let cursor: string | null = null;
    const records: NdtAsset[] = [];
    const type = this.activeTab;
    do {
      const page: NdtAssetListPage = await this.p.ndtAssetService.listAssetsPaged(this.currentContext, {
        assetType: type, search: this.assetSearch, cursor, limit: 100
      });
      if (records.length + page.items.length > maxRows) {
        throw new Error(`A exportação excede ${maxRows.toLocaleString('pt-BR')} equipamentos. Refine a pesquisa.`);
      }
      records.push(...page.items);
      cursor = page.nextCursor;
    } while (cursor);
    return this.calibrationFilter && this.tabHasCalibrationCertificate(type)
      ? records.filter(a => resolveCalibrationStatus(
        this.certificatesByAsset.get(a.id)?.expirationDate, new Date(), 30
      ) === this.calibrationFilter) : records;
  }

  private downloadNdtExport(bytes: Uint8Array, mime: string, filename: string): void {
    const blob = new Blob([new Uint8Array(bytes)], { type: mime });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  private async exportAssetsXlsx(): Promise<void> {
    try {
      const records = await this.collectAssetsForExport(10000);
      const bytes = buildNdtAssetsXlsxExport(records);
      this.downloadNdtExport(bytes,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'INSPECT-IA-Equipamentos-END.xlsx');
    } catch (error: any) {
      alert(error?.safeMessage || error?.message || 'Não foi possível exportar os equipamentos.');
    }
  }

  private async exportAssetsPdf(): Promise<void> {
    try {
      const records = await this.collectAssetsForExport(1500);
      const headers = ['TAG', 'Tipo', 'Fabricante / Modelo', 'Nº Série', 'Situação'];
      const body = [headers, ...records.map(a => [
        a.internalCode || '—', this.getTypeLabel(a.assetType),
        [a.manufacturer, a.model].filter(Boolean).join(' / ') || '—',
        a.serialNumber || '—', a.status || '—'
      ])];
      const bytes = await documentEngine.generatePdfBytes({
        pageSize: 'A4',
        pageOrientation: 'landscape',
        pageMargins: [28, 40, 28, 38],
        content: [
          {text: 'INSPECT IA', fontSize: 15, bold: true, margin: [0, 0, 0, 5]},
          {text: 'Gestão de Equipamentos END', fontSize: 11, margin: [0, 0, 0, 8]},
          {text: `${records.length} equipamento(s) • Emitido em ${new Date().toLocaleDateString('pt-BR')}`,
            fontSize: 8, color: '#5C5C5C', margin: [0, 0, 0, 12]},
          {table: {headerRows: 1, widths: [70, 120, '*', 100, 85],
            body: body.map((cells, n) => cells.map(value => ({
              text: String(value), fontSize: 8, bold: n === 0, margin: [2, 3, 2, 3]
            })))}, layout: 'lightHorizontalLines'}
        ],
        defaultStyle: { font: 'Roboto', fontSize: 9 },
        footer: (currentPage: number, pageCount: number) => ({
          text: `INSPECT IA • ${currentPage}/${pageCount}`,
          alignment: 'right', margin: [0, 0, 28, 0], fontSize: 8, color: '#5C5C5C'
        })
      });
      this.downloadNdtExport(bytes, 'application/pdf', 'INSPECT-IA-Equipamentos-END.pdf');
    } catch (error: any) {
      alert(error?.safeMessage || error?.message || 'Não foi possível exportar o PDF.');
    }
  }

  private isGlobalTab(tab: RegistryTab): boolean {
    return tab === PROCEDURES_TAB || tab === LP_CONSUMABLES_TAB ||
      tab === PM_CONSUMABLES_TAB || isCatalogTab(tab);
  }

  private resetGlobalPage(): void {
    this.globalPage = null;
    this.globalPageTab = null;
    this.globalCursor = null;
    this.globalPreviousCursors = [];
    this.globalError = null;
    ++this.globalRequest;
  }

  private async refreshGlobalPage(): Promise<void> {
    const tab = this.activeTab;
    if (!this.isGlobalTab(tab)) return;
    const seq = ++this.globalRequest;
    try {
      let service: any;
      const query: any = { search: this.globalSearch || (tab === PM_CONSUMABLES_TAB ? this.pmConsumableSearch : ""),
        cursor: this.globalCursor, limit: 25 };
      if (tab === PROCEDURES_TAB) {
        service = this.p.ndtProcedureService;
        query.category = this.procedureFilters.method || undefined;
        query.status = this.procedureFilters.status || undefined;
        query.code = this.procedureFilters.code || undefined;
        query.title = this.procedureFilters.title || undefined;
        query.referenceStandard = this.procedureFilters.referenceStandard || undefined;
      } else if (isCatalogTab(tab)) {
        service = this.p.ndtCatalogService;
        query.category = tab;
      } else if (tab === LP_CONSUMABLES_TAB) {
        service = this.p.lpConsumableService;
        query.category = this.lpConsumableTypeFilter || undefined;
      } else {
        service = this.p.pmConsumableService;
        query.category = this.pmConsumableTypeFilter || undefined;
      }
      const page: NdtGlobalListResult<any> = await service.listPaged(this.currentContext, query);
      if (seq !== this.globalRequest || tab !== this.activeTab) return;
      this.globalPage = page;
      this.globalPageTab = tab;
      this.globalError = null;
    } catch (error) {
      if (seq !== this.globalRequest || tab !== this.activeTab) return;
      console.error("[M21] Falha na consulta paginada do catálogo END", error);
      this.globalPage = null;
      this.globalPageTab = tab;
      this.globalError = "Não foi possível consultar os registros. Tente novamente.";
    }
    this.render();
  }

  private globalPageItems<T>(): T[] | null {
    return this.globalPage && this.globalPageTab === this.activeTab
      ? this.globalPage.items as T[] : null;
  }

  private isAssetTab(tab: RegistryTab): tab is NdtAssetType {
    return ['ULTRASONIC_THICKNESS_GAUGE', 'ULTRASONIC_TRANSDUCER', 'MAGNETIC_YOKE',
      'BSCAN_EQUIPMENT', 'CALIBRATION_BLOCK', 'PYROMETER'].includes(tab);
  }

  private async refreshAssetPage(): Promise<void> {
    if (!this.isAssetTab(this.activeTab)) return;
    const type = this.activeTab;
    const seq = ++this.assetPageRequest;
    try {
      const page: NdtAssetListPage = await this.p.ndtAssetService.listAssetsPaged(this.currentContext, {
        assetType: type, search: this.assetSearch, cursor: this.assetPageCursor, limit: 25
      });
      if (seq !== this.assetPageRequest || this.activeTab !== type) return;
      this.assetPage = page;
      this.assetPageError = null;
    } catch (error) {
      if (seq !== this.assetPageRequest || this.activeTab !== type) return;
      console.error('[M21] Falha na busca paginada END:', error);
      this.assetPage = null;
      this.assetPageError = "Não foi possível carregar a página de equipamentos.";
    }
    this.render();
  }

  private switchTab(tab: RegistryTab) {
    if (tab !== this.activeTab) this.resetGlobalPage();
    if (tab !== this.activeTab) {
      this.assetPage = null;
      this.assetPageCursor = null;
      this.assetPreviousCursors = [];
      this.assetPageError = null;
    }
    this.activeTab = tab;
    const url = new URL(window.location.href);
    url.searchParams.set('aba', tab);
    if (tab === DASHBOARD_TAB) url.searchParams.delete('grupo');
    else url.searchParams.set('grupo', this.activeGroup);
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    this.render();
    if (this.isAssetTab(tab)) void this.refreshAssetPage();
    else if (this.isGlobalTab(tab)) void this.refreshGlobalPage();
  }

  private openRegisterModal(editing: NdtAsset | null = null) {
    const existing = document.querySelector('#ndt-modal');
    if (existing) existing.remove();

    const typeLabel = this.getTypeLabel(this.activeTab as NdtAssetType);
    const td: any = (editing as any)?.technicalData || {};
    const currentCert = editing ? this.certificatesByAsset.get(editing.id) : null;

    // Dynamic fields based on active tab
    let extraFields = '';
    if (this.activeTab === 'ULTRASONIC_THICKNESS_GAUGE') {
      extraFields = `
        <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px;">
          <div><label class="frm-lbl">Faixa de Medição (Min) mm</label><input type="number" step="0.01" id="inp-range-min" class="frm-inp" value="${td.measurementRangeMin ?? ''}" /></div>
          <div><label class="frm-lbl">Faixa de Medição (Max) mm</label><input type="number" step="0.01" id="inp-range-max" class="frm-inp" value="${td.measurementRangeMax ?? ''}" /></div>
        </div>
      `;
    } else if (this.activeTab === 'ULTRASONIC_TRANSDUCER') {
      // TIPO and FREQUÊNCIA are independent: a frequency must never be
      // offered as a type (Bloco 17).
      const currentType = (td.transducerType || '') as string;
      const isStandardType = (TRANSDUCER_TYPES as readonly string[]).includes(currentType);
      const currentFreq = td.frequencyMHz as number | undefined;
      const standardFreq = isStandardFrequency(currentFreq);

      extraFields = `
        <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px;">
          <div>
            <label class="frm-lbl">Tipo do Transdutor *</label>
            <select id="inp-tr-type" class="frm-inp" required>
              <option value="">Selecione...</option>
              ${TRANSDUCER_TYPES.map(t => `<option value="${this.esc(t)}" ${isStandardType && currentType === t ? 'selected' : ''}>${this.esc(t)}</option>`).join('')}
              ${!isStandardType && currentType ? `<option value="${TRANSDUCER_TYPE_OTHER}" selected>${TRANSDUCER_TYPE_OTHER}</option>` : ''}
            </select>
          </div>
          <div>
            <label class="frm-lbl">Frequência (MHz) *</label>
            <select id="inp-freq" class="frm-inp" required>
              <option value="">Selecione...</option>
              ${TRANSDUCER_FREQUENCIES_MHZ.map(f => `<option value="${f}" ${standardFreq && currentFreq === f ? 'selected' : ''}>${this.esc(formatTransducerFrequency(f))}</option>`).join('')}
              <option value="${TRANSDUCER_FREQUENCY_OTHER}" ${currentFreq !== undefined && !standardFreq ? 'selected' : ''}>OUTRA</option>
            </select>
          </div>
        </div>
        <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px;">
          <div id="wrap-tr-type-other" style="display:${!isStandardType && currentType ? 'block' : 'none'};">
            <label class="frm-lbl">Outro Tipo de Transdutor</label>
            <input type="text" id="inp-tr-type-other" class="frm-inp" value="${!isStandardType ? this.esc(currentType) : ''}" />
          </div>
          <div id="wrap-freq-other" style="display:${currentFreq !== undefined && !standardFreq ? 'block' : 'none'};">
            <label class="frm-lbl">Frequência Personalizada (MHz)</label>
            <input type="number" step="0.01" id="inp-freq-other" class="frm-inp" value="${currentFreq !== undefined && !standardFreq ? currentFreq : ''}" />
          </div>
        </div>
        <div style="display:grid; grid-template-columns:2fr 1fr 2fr; gap:10px;">
          <div><label class="frm-lbl">Dimensão / Diâmetro</label><input type="number" step="0.01" id="inp-tr-dimension" class="frm-inp" value="${td.elementDiameter ?? ''}" /></div>
          <div>
            <label class="frm-lbl">Unidade</label>
            <select id="inp-tr-dimension-unit" class="frm-inp">
              ${TRANSDUCER_DIMENSION_UNITS.map(u => `<option value="${u}" ${(td.dimensionUnit || 'mm') === u ? 'selected' : ''}>${u}</option>`).join('')}
            </select>
          </div>
          <div><label class="frm-lbl">Método / Aplicação</label><input type="text" id="inp-tr-application" class="frm-inp" value="${this.esc(td.applicationType || TRANSDUCER_DEFAULT_APPLICATION)}" /></div>
        </div>
      `;
    } else if (this.activeTab === 'MAGNETIC_YOKE') {
      extraFields = `
        <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px;">
          <div><label class="frm-lbl">Alimentação</label><input type="text" id="inp-power" placeholder="AC/DC" class="frm-inp" value="${this.esc(td.powerType || '')}" /></div>
        </div>
      `;
    } else if (this.activeTab === 'PYROMETER') {
      extraFields = `
        <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px;">
          <div><label class="frm-lbl">Faixa de medição mínima (°C)</label><input type="number" step="0.01" id="inp-pyr-min" class="frm-inp" value="${td.measurementRangeMin ?? ''}"></div>
          <div><label class="frm-lbl">Faixa de medição máxima (°C)</label><input type="number" step="0.01" id="inp-pyr-max" class="frm-inp" value="${td.measurementRangeMax ?? ''}"></div>
          <div><label class="frm-lbl">Resolução (°C)</label><input type="number" min="0" step="0.001" id="inp-pyr-resolution" class="frm-inp" value="${td.resolution ?? ''}"></div>
          <div><label class="frm-lbl">Emissividade (0 a 1)</label><input type="number" min="0" max="1" step="0.01" id="inp-pyr-emissivity" class="frm-inp" value="${td.emissivity ?? ''}"></div>
          <div><label class="frm-lbl">Exatidão</label><input id="inp-pyr-accuracy" class="frm-inp" value="${this.esc(td.accuracy || '')}"></div>
          <div><label class="frm-lbl">Relação distância/ponto</label><input id="inp-pyr-distance" class="frm-inp" value="${this.esc(td.distanceToSpotRatio || '')}"></div>
        </div>
      `;
    } else if (this.activeTab === 'CALIBRATION_BLOCK') {
      extraFields = `
        <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px;">
          <div><label class="frm-lbl">Material</label><input type="text" id="inp-material" class="frm-inp" value="${this.esc(td.material || '')}" /></div>
          <div><label class="frm-lbl">Espessura Total (mm)</label><input type="number" step="0.01" id="inp-thick" class="frm-inp" value="${td.overallThickness ?? ''}" /></div>
        </div>
        <div style="margin-top:16px; border-top:1px solid var(--color-graphite-700); padding-top:12px;">
          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
            <label class="frm-lbl" style="margin:0; font-size:0.85rem; color: var(--ia-primary);">DEGRAUS</label>
            <button type="button" id="btn-add-step" style="background:var(--color-graphite-800); border:1px solid var(--color-text-muted); color:var(--color-text-muted); border-radius:4px; padding:2px 8px; font-size:0.75rem; cursor:pointer;">+ ADICIONAR DEGRAU</button>
          </div>
          <div id="steps-container" style="display:flex; flex-direction:column; gap:8px;"></div>
        </div>
      `;
    }

    // Calibration certificate belongs to the asset registry (single source of
    // truth), so it is captured here instead of only through the ad-hoc
    // "+ Cert" prompt — the ME report merely consumes it.
    const certificateFields = this.tabHasCalibrationCertificate(this.activeTab as NdtAssetType) ? `
      <div style="border-top:1px solid var(--color-graphite-700); padding-top:12px; margin-top:4px;">
        <label class="frm-lbl" style="color: var(--ia-primary); font-size:0.85rem;">CERTIFICADO DE CALIBRAÇÃO</label>
        <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-top:8px;">
          <div><label class="frm-lbl">Nº do Certificado</label><input type="text" id="inp-cert-number" class="frm-inp" value="${this.esc(currentCert?.certificateNumber || '')}" /></div>
          <div><label class="frm-lbl">Laboratório / Empresa Calibradora</label><input type="text" id="inp-cert-lab" class="frm-inp" value="${this.esc(currentCert?.laboratory || '')}" /></div>
        </div>
        <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-top:10px;">
          <div><label class="frm-lbl">Data da Calibração</label><input type="date" id="inp-cert-issue" class="frm-inp" value="${this.esc(String(currentCert?.issueDate || '').slice(0, 10))}" /></div>
          <div><label class="frm-lbl">Validade da Calibração</label><input type="date" id="inp-cert-expiry" class="frm-inp" value="${this.esc(String(currentCert?.expirationDate || '').slice(0, 10))}" /></div>
        </div>
        <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-top:10px; align-items:end;">
          <div><label class="frm-lbl">Anexo do Certificado (PDF / imagem)</label><input type="file" id="inp-cert-file" accept=".pdf,image/*" style="font-size:0.75rem; color:var(--color-graphite-400);" /></div>
          <div>
            <label class="frm-lbl">Status da Calibração</label>
            <div id="cert-status-preview" class="frm-inp" style="display:flex; align-items:center;">NÃO INFORMADA</div>
          </div>
        </div>
      </div>
    ` : '';

    const modal = document.createElement('div');
    modal.id = 'ndt-modal';
    modal.innerHTML = `
      <div style="position:fixed; inset:0; background:rgba(0,0,0,0.8); display:flex; align-items:center; justify-content:center; z-index:9999; backdrop-filter:blur(4px);">
        <div style="background:var(--color-graphite-800); border:1px solid var(--color-graphite-700); border-radius:10px; padding:24px; max-width:500px; width:90%; color: var(--color-text-primary); max-height: 90vh; overflow-y: auto;">
          <h3 style="margin-top:0; font-size:1.15rem; color:var(--color-text-muted);">${editing ? 'Editar' : 'Cadastrar'} ${typeLabel}</h3>

          <form id="ndt-form" style="display:flex; flex-direction:column; gap:12px; margin-top:16px;">
            <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px;">
              <div><label class="frm-lbl">Código Interno / Identificação *</label><input type="text" id="inp-code" required class="frm-inp" value="${this.esc(editing?.internalCode || '')}" /></div>
              <div><label class="frm-lbl">Número de Série *</label><input type="text" id="inp-serial" required class="frm-inp" value="${this.esc(editing?.serialNumber || '')}" /></div>
            </div>

            <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px;">
              <div><label class="frm-lbl">Fabricante</label><input type="text" id="inp-manufacturer" class="frm-inp" value="${this.esc(editing?.manufacturer || '')}" /></div>
              <div><label class="frm-lbl">Modelo</label><input type="text" id="inp-model" class="frm-inp" value="${this.esc(editing?.model || '')}" /></div>
            </div>

            ${extraFields}

            <div><label class="frm-lbl">Observações</label><textarea id="inp-notes" class="frm-inp" rows="2">${this.esc((editing as any)?.notes || '')}</textarea></div>
            ${certificateFields}

            <div id="ndt-form-error" style="display:none; color:var(--status-danger-solid); font-size:0.8rem;"></div>

            <div style="display:flex; justify-content:flex-end; gap:8px; margin-top:16px;">
              <button type="button" id="btn-cancel-modal" class="btn-cancel">Cancelar</button>
              <button type="submit" class="btn-save">Salvar Cadastro</button>
            </div>
          </form>
        </div>
      </div>
      <style>
        .frm-lbl { font-size:0.75rem; color:var(--color-graphite-400); font-weight:600; display:block; margin-bottom:4px; }
        .frm-inp { width:100%; padding:8px; background:var(--color-primary); border:1px solid var(--color-graphite-700); border-radius:6px; color: var(--color-text-primary); box-sizing: border-box; }
        .btn-cancel { background:var(--color-graphite-700); color: var(--color-text-primary); border:none; padding:8px 16px; border-radius:6px; cursor:pointer; }
        .btn-save { background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border:none; padding:8px 16px; border-radius:6px; font-weight:600; cursor:pointer; }
      </style>
    `;

    document.body.appendChild(modal);

    const btnAddStep = modal.querySelector('#btn-add-step');
    if (btnAddStep) {
      btnAddStep.addEventListener('click', () => {
        const container = modal.querySelector('#steps-container');
        if (container) {
          const stepDiv = document.createElement('div');
          stepDiv.className = 'step-row';
          stepDiv.style.display = 'grid';
          stepDiv.style.gridTemplateColumns = '1fr 2fr auto';
          stepDiv.style.gap = '8px';
          stepDiv.style.alignItems = 'center';
          stepDiv.innerHTML = `
            <input type="text" placeholder="ID Degrau" required class="frm-inp inp-step-number" />
            <div style="display:flex; align-items:center; gap:4px;">
              <input type="number" step="0.01" placeholder="Espessura" required class="frm-inp inp-step-thick" />
              <span style="color:var(--color-graphite-400); font-size:0.75rem;">mm</span>
            </div>
            <button type="button" class="btn-remove-step" style="background:none; border:none; color:var(--status-danger-solid); cursor:pointer;" title="Remover">&times;</button>
          `;
          stepDiv.querySelector('.btn-remove-step')?.addEventListener('click', () => stepDiv.remove());
          container.appendChild(stepDiv);
        }
      });
    }

    modal.querySelector('#btn-cancel-modal')?.addEventListener('click', () => modal.remove());

    // "OUTRO" / "OUTRA" reveal their own field — the standard lists never
    // absorb a custom value silently.
    const typeSelect = modal.querySelector('#inp-tr-type') as HTMLSelectElement | null;
    const typeOtherWrap = modal.querySelector('#wrap-tr-type-other') as HTMLElement | null;
    typeSelect?.addEventListener('change', () => {
      if (typeOtherWrap) typeOtherWrap.style.display = isOtherTransducerType(typeSelect.value) ? 'block' : 'none';
    });
    const freqSelect = modal.querySelector('#inp-freq') as HTMLSelectElement | null;
    const freqOtherWrap = modal.querySelector('#wrap-freq-other') as HTMLElement | null;
    freqSelect?.addEventListener('change', () => {
      if (freqOtherWrap) freqOtherWrap.style.display = freqSelect.value === TRANSDUCER_FREQUENCY_OTHER ? 'block' : 'none';
    });

    // Status is derived from the validity date as the user types it.
    let certificateAttachment: NdtCertificateAttachment | null = null;
    const expiryInput = modal.querySelector('#inp-cert-expiry') as HTMLInputElement | null;
    const statusPreview = modal.querySelector('#cert-status-preview') as HTMLElement | null;
    const refreshCertStatus = () => {
      if (!statusPreview) return;
      const expiry = expiryInput?.value || "";
      const status = resolveCalibrationStatus(expiry);
      statusPreview.textContent = CALIBRATION_STATUS_LABELS[status];
      statusPreview.style.color = status === 'EXPIRED' ? 'var(--status-danger-solid)' : status === 'VALID' ? '#4ade80' : 'var(--color-graphite-400)';
    };
    expiryInput?.addEventListener('change', refreshCertStatus);
    expiryInput?.addEventListener('input', refreshCertStatus);
    refreshCertStatus();

    modal.querySelector('#inp-cert-file')?.addEventListener('change', async (e) => {
      const input = e.target as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) { certificateAttachment = null; return; }
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      certificateAttachment = {
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        dataUrl,
        uploadedAt: new Date().toISOString()
      };
    });
    // Reads a field without assuming it is on screen: a field that a given
    // tab does not render must never blow up the whole submit.
    const val = (root: ParentNode, selector: string): string => {
      const el = root.querySelector(selector) as HTMLInputElement | null;
      return el ? el.value : '';
    };
    const num = (root: ParentNode, selector: string): number | undefined => {
      const raw = val(root, selector).trim();
      if (!raw) return undefined;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : undefined;
    };

    modal.querySelector('#ndt-form')?.addEventListener('submit', async (e) => {
      e.preventDefault();

      try {
        const payload: any = {
          assetType: this.activeTab,
          internalCode: val(modal, '#inp-code'),
          serialNumber: val(modal, '#inp-serial'),
          manufacturer: val(modal, '#inp-manufacturer'),
          model: val(modal, '#inp-model'),
          technicalData: {}
        };

        if (this.activeTab === 'ULTRASONIC_THICKNESS_GAUGE') {
          payload.technicalData.measurementRangeMin = num(modal, '#inp-range-min');
          payload.technicalData.measurementRangeMax = num(modal, '#inp-range-max');
        } else if (this.activeTab === 'ULTRASONIC_TRANSDUCER') {
          const selectedType = val(modal, '#inp-tr-type');
          payload.technicalData.transducerType = isOtherTransducerType(selectedType)
            ? val(modal, '#inp-tr-type-other').trim()
            : selectedType;

          const selectedFreq = val(modal, '#inp-freq');
          payload.technicalData.frequencyMHz = selectedFreq === TRANSDUCER_FREQUENCY_OTHER
            ? num(modal, '#inp-freq-other')
            : (selectedFreq ? Number(selectedFreq) : undefined);

          payload.technicalData.elementDiameter = num(modal, '#inp-tr-dimension');
          payload.technicalData.dimensionUnit = val(modal, '#inp-tr-dimension-unit') || 'mm';
          payload.technicalData.applicationType = val(modal, '#inp-tr-application');
        } else if (this.activeTab === 'MAGNETIC_YOKE') {
          payload.technicalData.powerType = val(modal, '#inp-power');
        } else if (this.activeTab === 'PYROMETER') {
          payload.technicalData.measurementRangeMin = num(modal, '#inp-pyr-min');
          payload.technicalData.measurementRangeMax = num(modal, '#inp-pyr-max');
          payload.technicalData.measurementUnit = '°C';
          payload.technicalData.resolution = num(modal, '#inp-pyr-resolution');
          payload.technicalData.emissivity = num(modal, '#inp-pyr-emissivity');
          payload.technicalData.accuracy = val(modal, '#inp-pyr-accuracy');
          payload.technicalData.distanceToSpotRatio = val(modal, '#inp-pyr-distance');
        } else if (this.activeTab === 'CALIBRATION_BLOCK') {
          payload.technicalData.material = val(modal, '#inp-material');
          payload.technicalData.overallThickness = num(modal, '#inp-thick');

          const stepRows = Array.from(modal.querySelectorAll('.step-row'));
          payload.steps = stepRows.map((row, i) => ({
            id: 'STEP-' + Date.now() + '-' + i,
            stepNumber: val(row, '.inp-step-number'),
            nominalThickness: num(row, '.inp-step-thick') ?? 0,
            unit: 'mm',
            sequence: i + 1,
            notes: ''
          }));
        }

        payload.notes = val(modal, '#inp-notes');

        const saved = editing
          ? await this.p.ndtAssetService.updateAsset(this.currentContext, editing.id, payload)
          : await this.p.ndtAssetService.createAsset(this.currentContext, payload);

        // The certificate is registered against the asset itself — the ME
        // report reads it from here, it is never retyped in the report.
        // Certificates are append-only history, so a new one is added only
        // when the data actually changed.
        const certNumber = val(modal, '#inp-cert-number').trim();
        const certChanged = !currentCert ||
          currentCert.certificateNumber !== certNumber ||
          String(currentCert.issueDate || '').slice(0, 10) !== val(modal, '#inp-cert-issue') ||
          String(currentCert.expirationDate || '').slice(0, 10) !== val(modal, '#inp-cert-expiry') ||
          (currentCert.laboratory || '') !== val(modal, '#inp-cert-lab');

        if (certNumber && certChanged) {
          await this.p.ndtAssetService.addCertificate(this.currentContext, saved.id, {
            certificateType: 'CALIBRATION',
            certificateNumber: certNumber,
            issueDate: val(modal, '#inp-cert-issue'),
            expirationDate: val(modal, '#inp-cert-expiry') || undefined,
            laboratory: val(modal, '#inp-cert-lab'),
            attachment: certificateAttachment
          });
        }

        modal.remove();
        this.loadAssets();
      } catch (err: any) {
        console.error('[END CADASTRO] Falha ao salvar o equipamento', err);
        const box = modal.querySelector('#ndt-form-error') as HTMLElement | null;
        const message = err?.safeMessage || err?.message || 'Erro ao salvar o cadastro.';
        if (box) {
          box.style.display = 'block';
          box.textContent = message;
        } else {
          alert(message);
        }
      }
    });
  }

  private async inactivateAsset(id: string) {
    if (confirm("Tem certeza que deseja INATIVAR este equipamento? Ele não aparecerá mais para novos relatórios.")) {
      try {
        await this.p.ndtAssetService.inactivateAsset(this.currentContext, id, "Inativado via UI");
        this.loadAssets();
      } catch(e: any) { alert(e.message); }
    }
  }
  
  private async deleteAsset(id: string) {
    const asset = (this.assets[this.activeTab as NdtAssetType] || []).find(a => a.id === id);
    const label = asset ? (asset.internalCode || asset.serialNumber || id) : id;
    if (!confirm(`Excluir definitivamente "${label}"? Esta ação não pode ser desfeita.\n\nEquipamentos com histórico de calibração ou já utilizados em relatórios não podem ser excluídos — nesses casos, utilize Inativar.`)) return;
    try {
      await this.p.ndtAssetService.deleteAsset(this.currentContext, id, "Excluído via Cadastro de Equipamentos de END");
      this.loadAssets();
    } catch (e: any) {
      alert(e?.safeMessage || e?.message || "Erro ao excluir o equipamento.");
    }
  }

  private async reactivateAsset(id: string) {
    if (confirm("Deseja reativar este equipamento?")) {
      try {
        await this.p.ndtAssetService.reactivateAsset(this.currentContext, id);
        this.loadAssets();
      } catch(e: any) { alert(e.message); }
    }
  }

  private async addCertificate(id: string) {
    const certNum = prompt("Número do Certificado:");
    if (!certNum) return;
    const expDate = prompt("Data de Vencimento (YYYY-MM-DD):");
    try {
      await this.p.ndtAssetService.addCertificate(this.currentContext, id, {
        certificateNumber: certNum,
        expirationDate: expDate || undefined
      });
      alert("Certificado vinculado com sucesso!");
      this.loadAssets(); // refresh status
    } catch (e: any) { alert(e.message); }
  }

  // --- PROCEDIMENTOS DE END (Bloco 14) ---

  private esc(value: unknown): string {
    return String(value ?? "")
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#039;");
  }

  /** Campo numérico vazio vira `null` (não preenchido), nunca `0` ou `NaN`. */
  private parseOptionalNumber(raw: string): number | null {
    const trimmed = (raw || "").trim();
    if (!trimmed) return null;
    const value = Number(trimmed);
    return Number.isFinite(value) ? value : null;
  }

  private renderProceduresTab(): string {
    const canCreate = contextStore.hasPermission(PermissionCode.NdtRegistryCreate);
    const canUpdate = contextStore.hasPermission(PermissionCode.NdtRegistryUpdate);
    const canToggle = contextStore.hasPermission(PermissionCode.NdtRegistryInactivate);
    const canDelete = contextStore.hasPermission(PermissionCode.NdtRegistryDelete);
    const f = this.procedureFilters;
    const visibleProcedures = this.globalPageItems<NdtProcedure>() || this.procedures;

    const filters = `
      <div class="proc-filters">
        <div>
          <label class="frm-lbl">Método</label>
          <select id="flt-proc-method" class="frm-inp">
            <option value="">Todos</option>
            ${NDT_METHODS.map(m => `<option value="${m}" ${f.method === m ? "selected" : ""}>${this.esc(NDT_METHOD_LABELS[m])}</option>`).join("")}
          </select>
        </div>
        <div><label class="frm-lbl">Código</label><input id="flt-proc-code" class="frm-inp" value="${this.esc(f.code || "")}" placeholder="Buscar por código" /></div>
        <div><label class="frm-lbl">Título</label><input id="flt-proc-title" class="frm-inp" value="${this.esc(f.title || "")}" placeholder="Buscar por título" /></div>
        <div><label class="frm-lbl">Norma</label><input id="flt-proc-standard" class="frm-inp" value="${this.esc(f.referenceStandard || "")}" placeholder="Buscar por norma" /></div>
        <div>
          <label class="frm-lbl">Status</label>
          <select id="flt-proc-status" class="frm-inp">
            <option value="">Todos</option>
            <option value="ACTIVE" ${f.status === "ACTIVE" ? "selected" : ""}>Ativo</option>
            <option value="INACTIVE" ${f.status === "INACTIVE" ? "selected" : ""}>Inativo</option>
          </select>
        </div>
      </div>
    `;

    if (this.procedureError) {
      return `${filters}<div class="empty-state"><h3 style="margin:0; color: var(--status-danger-solid);">${this.esc(this.procedureError)}</h3></div>`;
    }

    if (visibleProcedures.length === 0) {
      return `
        ${filters}
        <div class="empty-state">
          <h3 style="margin: 0 0 6px 0; color: var(--color-text-primary);">Nenhum procedimento de END encontrado</h3>
          <p style="margin:0; font-size:0.85rem;">Cadastre os procedimentos de ME, LP, PM e B-SCAN para que fiquem disponíveis nos relatórios.</p>
        </div>`;
    }

    return `
      ${filters}
      <table class="data-table">
        <thead>
          <tr><th>Método</th><th>Código</th><th>Título</th><th>Revisão</th><th>Norma de Referência</th><th>Status</th><th>Ações</th></tr>
        </thead>
        <tbody>
          ${visibleProcedures.map(p => `
            <tr>
              <td><span class="method-badge">${this.esc(p.method)}</span></td>
              <td style="font-weight:600; color: var(--color-text-muted);">${this.esc(p.code)}</td>
              <td>${this.esc(p.title)}</td>
              <td>${this.esc(p.revision || "-")}</td>
              <td>${this.esc(p.referenceStandard || "-")}</td>
              <td><span class="status-badge ${p.status === "ACTIVE" ? "status-valid" : "status-inactive"}">${p.status === "ACTIVE" ? "ATIVO" : "INATIVO"}</span></td>
              <td style="white-space:nowrap;">
                <button type="button" class="btn-link btn-proc-view" data-id="${p.id}">Visualizar</button>
                ${canUpdate ? `<button type="button" class="btn-link btn-proc-edit" data-id="${p.id}">Editar</button>` : ""}
                ${canToggle ? `<button type="button" class="btn-link btn-proc-toggle" data-id="${p.id}" style="color:${p.status === "ACTIVE" ? "var(--status-danger-solid)" : "#4ade80"};">${p.status === "ACTIVE" ? "Inativar" : "Ativar"}</button>` : ""}
                ${canDelete ? `<button type="button" class="btn-link btn-proc-delete" data-id="${p.id}" style="color:var(--status-danger-solid);">Excluir</button>` : ""}
              </td>
            </tr>
          `).join("")}
        </tbody>
      </table>
      <!-- UX Correção: mensagem redundante removida; botão + Cadastrar já oculto via !canCreate -->
    `;
  }

  private attachProcedureEvents() {
    const applyFilter = async (patch: Partial<NdtProcedureFilters>) => {
      this.procedureFilters = { ...this.procedureFilters, ...patch };
      this.resetGlobalPage();
      await this.loadProcedures();
      this.render();
      void this.refreshGlobalPage();
    };

    this.querySelector('#flt-proc-method')?.addEventListener('change', (e) => applyFilter({ method: (e.target as HTMLSelectElement).value as NdtMethod | "" }));
    this.querySelector('#flt-proc-status')?.addEventListener('change', (e) => applyFilter({ status: (e.target as HTMLSelectElement).value as any }));
    this.querySelector('#flt-proc-code')?.addEventListener('change', (e) => applyFilter({ code: (e.target as HTMLInputElement).value }));
    this.querySelector('#flt-proc-title')?.addEventListener('change', (e) => applyFilter({ title: (e.target as HTMLInputElement).value }));
    this.querySelector('#flt-proc-standard')?.addEventListener('change', (e) => applyFilter({ referenceStandard: (e.target as HTMLInputElement).value }));

    this.querySelectorAll('.btn-proc-view').forEach(b => b.addEventListener('click', (e) => {
      const id = (e.currentTarget as HTMLElement).dataset['id']!;
      this.openProcedureModal(this.procedures.find(p => p.id === id) || null, true);
    }));
    this.querySelectorAll('.btn-proc-edit').forEach(b => b.addEventListener('click', (e) => {
      const id = (e.currentTarget as HTMLElement).dataset['id']!;
      this.openProcedureModal(this.procedures.find(p => p.id === id) || null, false);
    }));
    this.querySelectorAll('.btn-proc-toggle').forEach(b => b.addEventListener('click', (e) => {
      this.toggleProcedureStatus((e.currentTarget as HTMLElement).dataset['id']!);
    }));
    this.querySelectorAll('.btn-proc-delete').forEach(b => b.addEventListener('click', (e) => {
      this.deleteProcedure((e.currentTarget as HTMLElement).dataset['id']!);
    }));
  }

  private async toggleProcedureStatus(id: string) {
    const procedure = this.procedures.find(p => p.id === id);
    if (!procedure) return;
    const next = procedure.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    const message = next === 'INACTIVE'
      ? `Inativar o procedimento ${procedure.code}? Ele deixará de aparecer para novos relatórios, mas continua visível nos relatórios que já o utilizam.`
      : `Reativar o procedimento ${procedure.code}?`;
    if (!confirm(message)) return;
    try {
      await this.p.ndtProcedureService.setStatus(this.currentContext, id, next);
      await this.loadProcedures();
      this.render();
    } catch (e: any) {
      alert(e?.safeMessage || e?.message || "Erro ao alterar o status do procedimento.");
    }
  }

  private async deleteProcedure(id: string) {
    const procedure = this.procedures.find(p => p.id === id);
    if (!procedure) return;
    if (!confirm(`Excluir definitivamente o procedimento ${procedure.code}? Esta ação não pode ser desfeita.`)) return;
    try {
      await this.p.ndtProcedureService.delete(this.currentContext, id);
      await this.loadProcedures();
      this.render();
    } catch (e: any) {
      alert(e?.safeMessage || e?.message || "Erro ao excluir o procedimento.");
    }
  }

  private openProcedureModal(existing: NdtProcedure | null, readOnly: boolean) {
    const old = document.querySelector('#ndt-procedure-modal');
    if (old) old.remove();

    let attachments: NdtProcedureAttachment[] = existing ? [...existing.attachments] : [];
    const dis = readOnly ? 'disabled' : '';
    const title = existing ? (readOnly ? `Procedimento ${existing.code}` : `Editar Procedimento ${existing.code}`) : 'Cadastrar Procedimento de END';

    const modal = document.createElement('div');
    modal.id = 'ndt-procedure-modal';
    modal.innerHTML = `
      <div style="position:fixed; inset:0; background:rgba(0,0,0,0.8); display:flex; align-items:center; justify-content:center; z-index:9999; backdrop-filter:blur(4px);">
        <div style="background:var(--color-graphite-800); border:1px solid var(--color-graphite-700); border-radius:10px; padding:24px; max-width:720px; width:94%; color: var(--color-text-primary); max-height:90vh; overflow-y:auto;">
          <h3 style="margin-top:0; font-size:1.15rem; color:var(--color-text-muted);">${this.esc(title)}</h3>
          <form id="proc-form" style="display:flex; flex-direction:column; gap:12px; margin-top:16px;">
            <div style="display:grid; grid-template-columns:1fr 1fr 1fr; gap:10px;">
              <div>
                <label class="frm-lbl">Método END *</label>
                <select id="inp-proc-method" class="frm-inp" ${dis} required>
                  ${NDT_METHODS.map(m => `<option value="${m}" ${existing?.method === m ? "selected" : ""}>${this.esc(NDT_METHOD_LABELS[m])}</option>`).join("")}
                </select>
              </div>
              <div><label class="frm-lbl">Código do Procedimento *</label><input id="inp-proc-code" class="frm-inp" ${dis} required value="${this.esc(existing?.code || "")}" /></div>
              <div><label class="frm-lbl">Revisão</label><input id="inp-proc-revision" class="frm-inp" ${dis} value="${this.esc(existing?.revision || "")}" /></div>
            </div>

            <div><label class="frm-lbl">Título / Nome do Procedimento *</label><input id="inp-proc-title" class="frm-inp" ${dis} required value="${this.esc(existing?.title || "")}" /></div>

            <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px;">
              <div><label class="frm-lbl">Data de Emissão</label><input type="date" id="inp-proc-issue" class="frm-inp" ${dis} value="${this.esc(existing?.issueDate || "")}" /></div>
              <div><label class="frm-lbl">Data de Validade</label><input type="date" id="inp-proc-validity" class="frm-inp" ${dis} value="${this.esc(existing?.validityDate || "")}" /></div>
            </div>

            <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px;">
              <div><label class="frm-lbl">Norma de Referência</label><input id="inp-proc-standard" class="frm-inp" ${dis} value="${this.esc(existing?.referenceStandard || "")}" /></div>
              <div><label class="frm-lbl">Critério de Aceitação</label><input id="inp-proc-criteria" class="frm-inp" ${dis} value="${this.esc(existing?.acceptanceCriteria || "")}" /></div>
            </div>

            <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px;">
              <div>
                <label class="frm-lbl">Técnica Aplicável</label>
                <select id="inp-proc-technique" class="frm-inp" ${dis}>
                  <option value="">Não aplicável</option>
                  ${ME_MEASUREMENT_TECHNIQUES.map(t => `<option value="${this.esc(t)}" ${existing?.defaultTechnique === t ? "selected" : ""}>${this.esc(t)}</option>`).join("")}
                </select>
                <small id="proc-technique-hint" style="color:var(--color-graphite-400); font-size:0.7rem;">Opções padronizadas de ME. Para LP/PM/B-SCAN, use "Não aplicável" quando não houver técnica correspondente.</small>
              </div>
              <div>
                <label class="frm-lbl">Status</label>
                <select id="inp-proc-status" class="frm-inp" ${dis}>
                  <option value="ACTIVE" ${existing?.status !== "INACTIVE" ? "selected" : ""}>Ativo</option>
                  <option value="INACTIVE" ${existing?.status === "INACTIVE" ? "selected" : ""}>Inativo</option>
                </select>
              </div>
            </div>

            <div id="proc-method-a-block" style="display:${existing?.method === "ME" || (!existing) ? "grid" : "none"}; grid-template-columns:1fr 1fr 1fr; gap:10px; border:1px solid var(--color-graphite-700); border-radius:8px; padding:10px;">
              <div style="grid-column:1 / -1; font-size:0.75rem; color:var(--color-graphite-400); font-weight:600;">
                MÉTODO A — PARÂMETROS DA MEDIÇÃO EM ALTA TEMPERATURA (Vsa obrigatório quando este procedimento é usado num ME em alta temperatura)
              </div>
              <div>
                <label class="frm-lbl">Vsa — Velocidade de Referência (m/s)</label>
                <input type="number" step="any" id="inp-proc-vsa" class="frm-inp" ${dis} value="${existing?.hotCorrectionReferenceVelocityMs ?? ""}" placeholder="Ex.: 5900" />
              </div>
              <div>
                <label class="frm-lbl">k — Coeficiente de Variação (fixo)</label>
                <input type="number" step="any" id="inp-proc-k" class="frm-inp" value="${METHOD_A_COEFFICIENT_K}" disabled title="Valor oficial fixo do Método A — não é lido do cadastro." />
              </div>
              <div>
                <label class="frm-lbl">Temperatura de Referência (°C) (fixa)</label>
                <input type="number" step="any" id="inp-proc-tref" class="frm-inp" value="${METHOD_A_REFERENCE_TEMPERATURE_C}" disabled title="Valor oficial fixo do Método A — não é lido do cadastro." />
              </div>
              ${(existing?.hotCorrectionCoefficientK != null || existing?.hotCorrectionReferenceTemperatureC != null) ? `
              <div style="grid-column:1 / -1; font-size:0.7rem; color:var(--color-graphite-400);">
                Este Procedimento tem k=${existing?.hotCorrectionCoefficientK ?? "—"} / Tref=${existing?.hotCorrectionReferenceTemperatureC ?? "—"} gravado de uma versão anterior do Método A. Mantido só para leitura de relatórios já emitidos — o cálculo novo usa sempre k=${METHOD_A_COEFFICIENT_K} e Tref=${METHOD_A_REFERENCE_TEMPERATURE_C}.
              </div>` : ""}
            </div>

            <div><label class="frm-lbl">Descrição / Escopo</label><textarea id="inp-proc-description" class="frm-inp" rows="3" ${dis}>${this.esc(existing?.description || "")}</textarea></div>
            <div><label class="frm-lbl">Observações</label><textarea id="inp-proc-observations" class="frm-inp" rows="2" ${dis}>${this.esc(existing?.observations || "")}</textarea></div>

            <div style="border-top:1px solid var(--color-graphite-700); padding-top:12px;">
              <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
                <label class="frm-lbl" style="margin:0;">ARQUIVO DO PROCEDIMENTO</label>
                ${readOnly ? "" : `<input type="file" id="inp-proc-file" accept=".pdf,.doc,.docx,image/*" style="font-size:0.75rem; color:var(--color-graphite-400);" />`}
              </div>
              <div id="proc-attachments" style="display:flex; flex-direction:column; gap:6px;"></div>
            </div>

            <div id="proc-form-error" style="display:none; color:var(--status-danger-solid); font-size:0.8rem;"></div>

            <div style="display:flex; justify-content:flex-end; gap:8px; margin-top:8px;">
              <button type="button" id="btn-proc-cancel" class="btn-cancel">${readOnly ? "Fechar" : "Cancelar"}</button>
              ${readOnly ? "" : `<button type="submit" class="btn-save">Salvar Procedimento</button>`}
            </div>
          </form>
        </div>
      </div>
      <style>
        #ndt-procedure-modal .frm-lbl { font-size:0.75rem; color:var(--color-graphite-400); font-weight:600; display:block; margin-bottom:4px; }
        #ndt-procedure-modal .frm-inp { width:100%; padding:8px; background:var(--color-primary); border:1px solid var(--color-graphite-700); border-radius:6px; color: var(--color-text-primary); box-sizing:border-box; font-family:inherit; }
        #ndt-procedure-modal .btn-cancel { background:var(--color-graphite-700); color: var(--color-text-primary); border:none; padding:8px 16px; border-radius:6px; cursor:pointer; }
        #ndt-procedure-modal .btn-save { background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border:none; padding:8px 16px; border-radius:6px; font-weight:600; cursor:pointer; }
        #ndt-procedure-modal .att-row { display:flex; justify-content:space-between; align-items:center; gap:8px; background:var(--color-graphite-700); border-radius:6px; padding:6px 10px; font-size:0.8rem; }
      </style>
    `;
    document.body.appendChild(modal);

    const renderAttachments = () => {
      const box = modal.querySelector('#proc-attachments')!;
      box.innerHTML = attachments.length === 0
        ? `<span style="font-size:0.75rem; color:var(--color-graphite-400);">Nenhum arquivo anexado.</span>`
        : attachments.map(a => `
            <div class="att-row">
              <a href="${a.dataUrl}" download="${this.esc(a.fileName)}" style="color:var(--color-text-primary);">${this.esc(a.fileName)}</a>
              ${readOnly ? "" : `<button type="button" class="btn-remove-att" data-id="${a.id}" style="background:none; border:none; color:var(--status-danger-solid); cursor:pointer;">&times;</button>`}
            </div>`).join("");
      box.querySelectorAll('.btn-remove-att').forEach(b => b.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).dataset['id'];
        attachments = attachments.filter(a => a.id !== id);
        renderAttachments();
      }));
    };
    renderAttachments();

    // Método A só se aplica a END de Medição de Espessura (ME) — LP/PM/B-SCAN
    // não têm alta temperatura corrigida, então o bloco fica oculto para eles.
    modal.querySelector('#inp-proc-method')?.addEventListener('change', (e) => {
      const block = modal.querySelector('#proc-method-a-block') as HTMLElement;
      if (block) block.style.display = (e.target as HTMLSelectElement).value === "ME" ? "grid" : "none";
    });

    modal.querySelector('#inp-proc-file')?.addEventListener('change', async (e) => {
      const input = e.target as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) return;
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      attachments.push({
        id: `ATT-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        dataUrl,
        uploadedAt: new Date().toISOString()
      });
      input.value = "";
      renderAttachments();
    });

    modal.querySelector('#btn-proc-cancel')?.addEventListener('click', () => modal.remove());

    modal.querySelector('#proc-form')?.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (readOnly) return;
      const payload: Partial<NdtProcedure> = {
        method: (modal.querySelector('#inp-proc-method') as HTMLSelectElement).value as NdtMethod,
        code: (modal.querySelector('#inp-proc-code') as HTMLInputElement).value,
        title: (modal.querySelector('#inp-proc-title') as HTMLInputElement).value,
        revision: (modal.querySelector('#inp-proc-revision') as HTMLInputElement).value,
        issueDate: (modal.querySelector('#inp-proc-issue') as HTMLInputElement).value,
        validityDate: (modal.querySelector('#inp-proc-validity') as HTMLInputElement).value,
        referenceStandard: (modal.querySelector('#inp-proc-standard') as HTMLInputElement).value,
        acceptanceCriteria: (modal.querySelector('#inp-proc-criteria') as HTMLInputElement).value,
        defaultTechnique: (modal.querySelector('#inp-proc-technique') as HTMLSelectElement).value,
        status: (modal.querySelector('#inp-proc-status') as HTMLSelectElement).value as any,
        description: (modal.querySelector('#inp-proc-description') as HTMLTextAreaElement).value,
        observations: (modal.querySelector('#inp-proc-observations') as HTMLTextAreaElement).value,
        attachments,
        // k e Tref NÃO são mais enviados: são constantes fixas do Método A
        // (ver bloco somente-leitura acima), não dado do cadastro. Omitir a
        // chave aqui faz `update()` preservar o valor histórico já gravado
        // num Procedimento antigo (merge), e `create()` grava null num
        // Procedimento novo — nenhum cálculo novo lê este campo de qualquer
        // forma.
        hotCorrectionReferenceVelocityMs: this.parseOptionalNumber((modal.querySelector('#inp-proc-vsa') as HTMLInputElement).value)
      };

      try {
        if (existing) {
          await this.p.ndtProcedureService.update(this.currentContext, existing.id, payload);
        } else {
          await this.p.ndtProcedureService.create(this.currentContext, payload);
        }
        modal.remove();
        await this.loadProcedures();
        this.render();
      } catch (err: any) {
        const box = modal.querySelector('#proc-form-error') as HTMLElement;
        box.style.display = 'block';
        box.textContent = err?.safeMessage || err?.message || "Erro ao salvar o procedimento.";
      }
    });
  }

  // --- CATÁLOGOS TÉCNICOS: NORMAS / CRITÉRIOS / ACOPLANTES (Bloco 15) ---

  private renderCatalogTab(catalogKey: NdtCatalogKey): string {
    const canUpdate = contextStore.hasPermission(PermissionCode.NdtRegistryUpdate);
    const canToggle = contextStore.hasPermission(PermissionCode.NdtRegistryInactivate);
    const canDelete = contextStore.hasPermission(PermissionCode.NdtRegistryDelete);
    const items = (this.globalPageItems<NdtCatalogItem>() || this.catalogItems).filter(i => i.catalogKey === catalogKey);

    if (items.length === 0) {
      return `
        <div class="empty-state">
          <h3 style="margin: 0 0 6px 0; color: var(--color-text-primary);">Nenhum registro em ${this.esc(NDT_CATALOG_LABELS[catalogKey])}</h3>
          <p style="margin:0; font-size:0.85rem;">Cadastre os registros para que fiquem disponíveis como opção nos relatórios de END.</p>
        </div>`;
    }

    return `
      <table class="data-table catalog-table">
        <thead>
          <tr><th class="col-code">Código</th><th class="col-name">Nome</th><th class="col-desc">Descrição</th><th class="col-methods">Métodos</th><th class="col-status">Status</th><th class="col-actions">Ações</th></tr>
        </thead>
        <tbody>
          ${items.map(i => `
            <tr>
              <td class="col-code" style="font-weight:700; color: var(--ia-fg, #FFFFFF);">${this.esc(i.code || "-")}</td>
              <td class="col-name" style="font-weight:600; color: var(--ia-fg, #FFFFFF);">${this.esc(i.name)}</td>
              <td class="col-desc">${this.esc(i.description || "-")}</td>
              <td class="col-methods"><span class="method-badges">${i.applicableMethods.length === 0 ? '<span class="method-badge">TODOS</span>' : i.applicableMethods.map(m => `<span class="method-badge">${this.esc(m)}</span>`).join("")}</span></td>
              <td class="col-status"><span class="status-badge ${i.status === "ACTIVE" ? "status-valid" : "status-inactive"}">${i.status === "ACTIVE" ? "ATIVO" : "INATIVO"}</span></td>
              <td class="col-actions">
                ${canUpdate ? `<button type="button" class="btn-link btn-cat-edit" data-id="${i.id}">Editar</button>` : ""}
                ${canToggle ? `<button type="button" class="btn-link btn-cat-toggle" data-id="${i.id}" style="color:${i.status === "ACTIVE" ? "var(--status-danger-solid)" : "#4ade80"};">${i.status === "ACTIVE" ? "Inativar" : "Ativar"}</button>` : ""}
                ${canDelete ? `<button type="button" class="btn-link btn-cat-delete" data-id="${i.id}" style="color:var(--status-danger-solid);">Excluir</button>` : ""}
              </td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    `;
  }

  private attachCatalogEvents(catalogKey: NdtCatalogKey) {
    this.querySelectorAll('.btn-cat-edit').forEach(b => b.addEventListener('click', (e) => {
      const id = (e.currentTarget as HTMLElement).dataset['id']!;
      this.openCatalogModal(catalogKey, this.catalogItems.find(i => i.id === id) || null);
    }));
    this.querySelectorAll('.btn-cat-toggle').forEach(b => b.addEventListener('click', async (e) => {
      const id = (e.currentTarget as HTMLElement).dataset['id']!;
      const item = this.catalogItems.find(i => i.id === id);
      if (!item) return;
      const next = item.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      if (!confirm(next === 'INACTIVE'
        ? `Inativar "${item.name}"? Ele deixará de aparecer para novos relatórios, mas continua nos relatórios que já o utilizam.`
        : `Reativar "${item.name}"?`)) return;
      try {
        await this.p.ndtCatalogService.setStatus(this.currentContext, id, next);
        await this.loadCatalogItems();
        this.render();
      } catch (err: any) {
        alert(err?.safeMessage || err?.message || "Erro ao alterar o status do registro.");
      }
    }));
    this.querySelectorAll('.btn-cat-delete').forEach(b => b.addEventListener('click', async (e) => {
      const id = (e.currentTarget as HTMLElement).dataset['id']!;
      const item = this.catalogItems.find(i => i.id === id);
      if (!item || !confirm(`Excluir definitivamente "${item.name}"?`)) return;
      try {
        await this.p.ndtCatalogService.delete(this.currentContext, id);
        await this.loadCatalogItems();
        this.render();
      } catch (err: any) {
        alert(err?.safeMessage || err?.message || "Erro ao excluir o registro.");
      }
    }));
  }

  // ---------------------------------------------------------------- CONSUMÍVEIS LP

  private renderLpConsumablesTab(): string {
    const canUpdate = contextStore.hasPermission(PermissionCode.NdtRegistryUpdate);
    const canToggle = contextStore.hasPermission(PermissionCode.NdtRegistryInactivate);
    const canDelete = contextStore.hasPermission(PermissionCode.NdtRegistryDelete);
    const items = (this.globalPageItems<LpConsumable>() || this.lpConsumables).filter(i => !this.lpConsumableTypeFilter || i.type === this.lpConsumableTypeFilter);

    const filter = `
      <div class="proc-filters" style="grid-template-columns: repeat(2, minmax(0, 260px));">
        <div>
          <label class="frm-lbl">Tipo</label>
          <select id="flt-lp-type" class="frm-inp">
            <option value="" ${this.lpConsumableTypeFilter === "" ? "selected" : ""}>Todos</option>
            ${LP_CONSUMABLE_TYPES.map(t => `<option value="${t}" ${this.lpConsumableTypeFilter === t ? "selected" : ""}>${this.esc(LP_CONSUMABLE_TYPE_LABELS[t])}</option>`).join("")}
          </select>
        </div>
      </div>`;

    if (items.length === 0) {
      return filter + `
        <div class="empty-state">
          <h3 style="margin: 0 0 6px 0; color: var(--color-text-primary);">Nenhum consumível LP cadastrado</h3>
          <p style="margin:0; font-size:0.85rem;">Cadastre penetrantes, removedores e reveladores (marca, produto, lote e validade) para que fiquem disponíveis no Relatório LP.</p>
        </div>`;
    }

    return filter + `
      <table class="data-table">
        <thead>
          <tr><th>Tipo</th><th>Marca</th><th>Produto</th><th>Lote</th><th>Validade</th><th>Status</th><th>Observações</th><th>Ações</th></tr>
        </thead>
        <tbody>
          ${items.map(i => {
            const expired = isLpConsumableExpired(i.validity);
            return `
            <tr>
              <td><span class="method-badge">${this.esc(LP_CONSUMABLE_TYPE_LABELS[i.type])}</span></td>
              <td style="font-weight:600; color: var(--color-text-muted);">${this.esc(i.brand)}</td>
              <td>${this.esc(i.product)}</td>
              <td>${this.esc(i.lot)}</td>
              <td><span class="status-badge ${!i.validity ? "status-inactive" : expired ? "status-expired" : "status-valid"}">${i.validity ? (expired ? "⚠ " : "") + this.esc(formatCivilDateBR(i.validity)) : "NÃO INFORMADA"}</span></td>
              <td><span class="status-badge ${i.status === "ACTIVE" ? "status-valid" : "status-inactive"}">${i.status === "ACTIVE" ? "ATIVO" : "INATIVO"}</span></td>
              <td style="font-size:0.82rem;">${this.esc(i.observations || "-")}</td>
              <td style="white-space:nowrap;">
                ${canUpdate ? `<button type="button" class="btn-link btn-lpc-edit" data-id="${i.id}">Editar</button>` : ""}
                ${canToggle ? `<button type="button" class="btn-link btn-lpc-toggle" data-id="${i.id}" style="color:${i.status === "ACTIVE" ? "var(--status-danger-solid)" : "#4ade80"};">${i.status === "ACTIVE" ? "Inativar" : "Ativar"}</button>` : ""}
                ${canDelete ? `<button type="button" class="btn-link btn-lpc-delete" data-id="${i.id}" style="color:var(--status-danger-solid);">Excluir</button>` : ""}
              </td>
            </tr>`;
          }).join("")}
        </tbody>
      </table>`;
  }

  private attachLpConsumableEvents() {
    this.querySelector('#flt-lp-type')?.addEventListener('change', (e) => {
      this.lpConsumableTypeFilter = (e.target as HTMLSelectElement).value as any;
      this.resetGlobalPage();
      this.render();
      void this.refreshGlobalPage();
    });
    this.querySelectorAll('.btn-lpc-edit').forEach(b => b.addEventListener('click', (e) => {
      const id = (e.currentTarget as HTMLElement).dataset['id']!;
      this.openLpConsumableModal(this.lpConsumables.find(i => i.id === id) || null);
    }));
    this.querySelectorAll('.btn-lpc-toggle').forEach(b => b.addEventListener('click', async (e) => {
      const id = (e.currentTarget as HTMLElement).dataset['id']!;
      const item = this.lpConsumables.find(i => i.id === id);
      if (!item) return;
      const next = item.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      const label = `${item.brand} — ${item.product} (Lote ${item.lot})`;
      if (!confirm(next === 'INACTIVE'
        ? `Inativar "${label}"? Deixará de aparecer para novos relatórios, mas continua nos relatórios que já o utilizam.`
        : `Reativar "${label}"?`)) return;
      try {
        await this.p.lpConsumableService.setStatus(this.currentContext, id, next);
        await this.loadLpConsumables();
        this.render();
      } catch (err: any) {
        alert(err?.safeMessage || err?.message || "Erro ao alterar o status do consumível.");
      }
    }));
    this.querySelectorAll('.btn-lpc-delete').forEach(b => b.addEventListener('click', async (e) => {
      const id = (e.currentTarget as HTMLElement).dataset['id']!;
      const item = this.lpConsumables.find(i => i.id === id);
      if (!item || !confirm(`Excluir definitivamente "${item.brand} — ${item.product} (Lote ${item.lot})"?`)) return;
      try {
        await this.p.lpConsumableService.delete(this.currentContext, id);
        await this.loadLpConsumables();
        this.render();
      } catch (err: any) {
        alert(err?.safeMessage || err?.message || "Erro ao excluir o consumível.");
      }
    }));
  }

  private openLpConsumableModal(existing: LpConsumable | null) {
    const old = document.querySelector('#ndt-lpc-modal');
    if (old) old.remove();

    const modal = document.createElement('div');
    modal.id = 'ndt-lpc-modal';
    modal.innerHTML = `
      <div style="position:fixed; inset:0; background:rgba(0,0,0,0.8); display:flex; align-items:center; justify-content:center; z-index:9999; backdrop-filter:blur(4px);">
        <div style="background:var(--color-graphite-800); border:1px solid var(--color-graphite-700); border-radius:10px; padding:24px; max-width:640px; width:94%; color: var(--color-text-primary); max-height:90vh; overflow-y:auto;">
          <h3 style="margin-top:0; font-size:1.15rem; color:var(--color-text-muted);">${existing ? "Editar Consumível LP" : "Cadastrar Consumível LP"}</h3>
          <form id="lpc-form" style="display:flex; flex-direction:column; gap:12px; margin-top:16px;">
            <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px;">
              <div><label class="frm-lbl">Tipo *</label>
                <select id="inp-lpc-type" class="frm-inp" ${existing ? "disabled" : ""}>
                  ${LP_CONSUMABLE_TYPES.map(t => `<option value="${t}" ${existing?.type === t ? "selected" : ""}>${this.esc(LP_CONSUMABLE_TYPE_LABELS[t])}</option>`).join("")}
                </select></div>
              <div><label class="frm-lbl">Marca *</label><input id="inp-lpc-brand" class="frm-inp" required value="${this.esc(existing?.brand || "")}" placeholder="Sherwin DUBL-CHEK" /></div>
            </div>
            <div style="display:grid; grid-template-columns:1fr 1fr 1fr; gap:10px;">
              <div><label class="frm-lbl">Produto *</label><input id="inp-lpc-product" class="frm-inp" required value="${this.esc(existing?.product || "")}" placeholder="D-100" /></div>
              <div><label class="frm-lbl">Lote *</label><input id="inp-lpc-lot" class="frm-inp" required value="${this.esc(existing?.lot || "")}" placeholder="24A03" /></div>
              <div><label class="frm-lbl">Validade</label><input id="inp-lpc-validity" class="frm-inp" type="date" value="${this.esc(existing?.validity || "")}" /></div>
            </div>
            <div><label class="frm-lbl">Observações</label><textarea id="inp-lpc-observations" class="frm-inp" rows="2">${this.esc(existing?.observations || "")}</textarea></div>
            <div style="max-width:220px;">
              <label class="frm-lbl">Status</label>
              <select id="inp-lpc-status" class="frm-inp">
                <option value="ACTIVE" ${existing?.status !== "INACTIVE" ? "selected" : ""}>Ativo</option>
                <option value="INACTIVE" ${existing?.status === "INACTIVE" ? "selected" : ""}>Inativo</option>
              </select>
            </div>
            <div id="lpc-form-error" style="display:none; color:var(--status-danger-solid); font-size:0.8rem;"></div>
            <div style="display:flex; justify-content:flex-end; gap:8px; margin-top:8px;">
              <button type="button" id="btn-lpc-cancel" class="btn-cancel">Cancelar</button>
              <button type="submit" class="btn-save">Salvar</button>
            </div>
          </form>
        </div>
      </div>
      <style>
        #ndt-lpc-modal .frm-lbl { font-size:0.75rem; color:var(--color-graphite-400); font-weight:600; display:block; margin-bottom:4px; }
        #ndt-lpc-modal .frm-inp { width:100%; padding:8px; background:var(--color-primary); border:1px solid var(--color-graphite-700); border-radius:6px; color: var(--color-text-primary); box-sizing:border-box; font-family:inherit; }
        #ndt-lpc-modal .btn-cancel { background:var(--color-graphite-700); color: var(--color-text-primary); border:none; padding:8px 16px; border-radius:6px; cursor:pointer; }
        #ndt-lpc-modal .btn-save { background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border:none; padding:8px 16px; border-radius:6px; font-weight:600; cursor:pointer; }
      </style>
    `;
    document.body.appendChild(modal);

    modal.querySelector('#btn-lpc-cancel')?.addEventListener('click', () => modal.remove());
    modal.querySelector('#lpc-form')?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const payload: Partial<LpConsumable> = {
        type: (modal.querySelector('#inp-lpc-type') as HTMLSelectElement).value as LpConsumableType,
        brand: (modal.querySelector('#inp-lpc-brand') as HTMLInputElement).value,
        product: (modal.querySelector('#inp-lpc-product') as HTMLInputElement).value,
        lot: (modal.querySelector('#inp-lpc-lot') as HTMLInputElement).value,
        validity: (modal.querySelector('#inp-lpc-validity') as HTMLInputElement).value,
        observations: (modal.querySelector('#inp-lpc-observations') as HTMLTextAreaElement).value,
        status: (modal.querySelector('#inp-lpc-status') as HTMLSelectElement).value as any
      };
      try {
        if (existing) {
          await this.p.lpConsumableService.update(this.currentContext, existing.id, payload);
        } else {
          await this.p.lpConsumableService.create(this.currentContext, payload);
        }
        modal.remove();
        await this.loadLpConsumables();
        this.render();
      } catch (err: any) {
        const box = modal.querySelector('#lpc-form-error') as HTMLElement;
        box.style.display = 'block';
        box.textContent = err?.safeMessage || err?.message || "Erro ao salvar o consumível.";
      }
    });
  }

  // ---------------------------------------------------------------- CONSUMÍVEIS PM

  private renderPmConsumablesTab(): string {
    const canUpdate = contextStore.hasPermission(PermissionCode.NdtRegistryUpdate);
    const canToggle = contextStore.hasPermission(PermissionCode.NdtRegistryInactivate);
    const canDelete = contextStore.hasPermission(PermissionCode.NdtRegistryDelete);
    const term = this.pmConsumableSearch.trim().toLowerCase();
    const items = (this.globalPageItems<PmConsumable>() || this.pmConsumables)
      .filter(i => !this.pmConsumableTypeFilter || i.type === this.pmConsumableTypeFilter)
      .filter(i => !term || [i.brand, i.product, i.lot].some(v => v.toLowerCase().includes(term)));

    const head = `
      <h2 style="margin:0 0 12px 0; font-size:0.95rem; font-weight:700; color: var(--ia-fg, #FFFFFF); letter-spacing:0.04em; text-transform:uppercase;">Consumíveis de Partículas Magnéticas</h2>
      <div class="proc-filters" style="grid-template-columns: minmax(0, 220px) minmax(0, 360px);">
        <div>
          <label class="frm-lbl">Tipo</label>
          <select id="flt-pm-type" class="frm-inp">
            <option value="" ${this.pmConsumableTypeFilter === "" ? "selected" : ""}>Todos</option>
            ${PM_CONSUMABLE_TYPES.map(t => `<option value="${t.code}" ${this.pmConsumableTypeFilter === t.code ? "selected" : ""}>${this.esc(t.label)}</option>`).join("")}
          </select>
        </div>
        <div>
          <label class="frm-lbl">Busca</label>
          <input id="flt-pm-search" class="frm-inp" placeholder="Buscar por marca, produto ou lote..." value="${this.esc(this.pmConsumableSearch)}" />
        </div>
      </div>`;

    if (items.length === 0) {
      return head + `
        <div class="empty-state">
          <h3 style="margin: 0 0 6px 0; color: var(--color-text-primary);">${this.pmConsumables.length === 0 ? "Nenhum consumível PM cadastrado" : "Nenhum consumível PM corresponde ao filtro"}</h3>
          <p style="margin:0; font-size:0.85rem;">Cadastre partículas magnéticas, tintas de contraste e veículos/suspensões (marca, produto, lote e validade) para o Relatório de Partículas Magnéticas.</p>
        </div>`;
    }

    return head + `
      <table class="data-table catalog-table">
        <thead>
          <tr><th>Tipo</th><th>Marca</th><th>Produto</th><th>Lote</th><th>Validade</th><th>Técnica / Aplicação</th><th>Status</th><th class="col-actions">Ações</th></tr>
        </thead>
        <tbody>
          ${items.map(i => {
            const expired = isPmConsumableExpired(i.validity);
            return `
            <tr>
              <td><span class="method-badge">${this.esc(formatPmConsumableType(i.type))}</span></td>
              <td style="font-weight:600; color: var(--ia-fg, #FFFFFF);">${this.esc(i.brand)}</td>
              <td>${this.esc(i.product)}</td>
              <td>${this.esc(i.lot)}</td>
              <td><span class="status-badge ${expired ? "status-expired" : "status-valid"}">${expired ? "⚠ " : ""}${this.esc(formatCivilDateBR(i.validity))}</span></td>
              <td style="font-size:0.82rem;">${this.esc(formatPmTechnique(i.technique) || "-")}</td>
              <td><span class="status-badge ${i.status === "ACTIVE" ? "status-valid" : "status-inactive"}">${i.status === "ACTIVE" ? "ATIVO" : "INATIVO"}</span></td>
              <td class="col-actions">
                ${canUpdate ? `<button type="button" class="btn-link btn-pmc-edit" data-id="${i.id}">Editar</button>` : ""}
                ${canToggle ? `<button type="button" class="btn-link btn-pmc-toggle" data-id="${i.id}" style="color:${i.status === "ACTIVE" ? "var(--status-danger-solid)" : "#4ade80"};">${i.status === "ACTIVE" ? "Inativar" : "Ativar"}</button>` : ""}
                ${canDelete ? `<button type="button" class="btn-link btn-pmc-delete" data-id="${i.id}" style="color:var(--status-danger-solid);">Excluir</button>` : ""}
              </td>
            </tr>`;
          }).join("")}
        </tbody>
      </table>`;
  }

  private attachPmConsumableEvents() {
    this.querySelector('#flt-pm-type')?.addEventListener('change', (e) => {
      this.pmConsumableTypeFilter = (e.target as HTMLSelectElement).value;
      this.resetGlobalPage();
      this.render();
      void this.refreshGlobalPage();
    });
    const search = this.querySelector('#flt-pm-search') as HTMLInputElement | null;
    search?.addEventListener('input', () => {
      this.pmConsumableSearch = search.value;
      this.resetGlobalPage();
      this.render();
      void this.refreshGlobalPage();
      const again = this.querySelector('#flt-pm-search') as HTMLInputElement | null;
      if (again) { again.focus(); again.setSelectionRange(again.value.length, again.value.length); }
    });
    this.querySelectorAll('.btn-pmc-edit').forEach(b => b.addEventListener('click', (e) => {
      const id = (e.currentTarget as HTMLElement).dataset['id']!;
      this.openPmConsumableModal(this.pmConsumables.find(i => i.id === id) || null);
    }));
    this.querySelectorAll('.btn-pmc-toggle').forEach(b => b.addEventListener('click', async (e) => {
      const id = (e.currentTarget as HTMLElement).dataset['id']!;
      const item = this.pmConsumables.find(i => i.id === id);
      if (!item) return;
      const next = item.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      const label = `${item.brand} — ${item.product} (Lote ${item.lot})`;
      if (!confirm(next === 'INACTIVE'
        ? `Inativar "${label}"? Deixará de aparecer para novos relatórios, mas continua nos relatórios que já o utilizam.`
        : `Reativar "${label}"?`)) return;
      try {
        await this.p.pmConsumableService.setStatus(this.currentContext, id, next);
        await this.loadPmConsumables();
        this.render();
      } catch (err: any) {
        alert(err?.safeMessage || err?.message || "Erro ao alterar o status do consumível.");
      }
    }));
    this.querySelectorAll('.btn-pmc-delete').forEach(b => b.addEventListener('click', async (e) => {
      const id = (e.currentTarget as HTMLElement).dataset['id']!;
      const item = this.pmConsumables.find(i => i.id === id);
      if (!item || !confirm(`Excluir definitivamente "${item.brand} — ${item.product} (Lote ${item.lot})"?`)) return;
      try {
        await this.p.pmConsumableService.delete(this.currentContext, id);
        await this.loadPmConsumables();
        this.render();
      } catch (err: any) {
        alert(err?.safeMessage || err?.message || "Erro ao excluir o consumível.");
      }
    }));
  }

  private openPmConsumableModal(existing: PmConsumable | null) {
    const old = document.querySelector('#ndt-pmc-modal');
    if (old) old.remove();

    const modal = document.createElement('div');
    modal.id = 'ndt-pmc-modal';
    modal.innerHTML = `
      <div style="position:fixed; inset:0; background:rgba(0,0,0,0.8); display:flex; align-items:center; justify-content:center; z-index:9999; backdrop-filter:blur(4px);">
        <div style="background:var(--color-graphite-800); border:1px solid var(--color-graphite-700); border-radius:10px; padding:24px; max-width:660px; width:94%; color: var(--color-text-primary); max-height:90vh; overflow-y:auto;">
          <h3 style="margin-top:0; font-size:1.15rem; color:var(--color-text-muted);">${existing ? "Editar Consumível PM" : "Cadastrar Consumível PM"}</h3>
          <form id="pmc-form" style="display:flex; flex-direction:column; gap:12px; margin-top:16px;">
            <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px;">
              <div><label class="frm-lbl">Tipo *</label>
                <select id="inp-pmc-type" class="frm-inp" ${existing ? "disabled" : ""}>
                  ${PM_CONSUMABLE_TYPES.map(t => `<option value="${t.code}" ${existing?.type === t.code ? "selected" : ""}>${this.esc(t.label)}</option>`).join("")}
                </select></div>
              <div><label class="frm-lbl">Marca *</label><input id="inp-pmc-brand" class="frm-inp" required value="${this.esc(existing?.brand || "")}" placeholder="Magnaflux" /></div>
            </div>
            <div style="display:grid; grid-template-columns:2fr 1fr; gap:10px;">
              <div><label class="frm-lbl">Produto / Nome Comercial *</label><input id="inp-pmc-product" class="frm-inp" required value="${this.esc(existing?.product || "")}" placeholder="7HF" /></div>
              <div><label class="frm-lbl">Lote *</label><input id="inp-pmc-lot" class="frm-inp" required value="${this.esc(existing?.lot || "")}" placeholder="24A03" /></div>
            </div>
            <div style="display:grid; grid-template-columns:1fr 1fr 1fr; gap:10px;">
              <div><label class="frm-lbl">Data de Fabricação</label><input id="inp-pmc-manufacturing" class="frm-inp" type="date" value="${this.esc(existing?.manufacturingDate || "")}" /></div>
              <div><label class="frm-lbl">Data de Validade *</label><input id="inp-pmc-validity" class="frm-inp" type="date" required value="${this.esc(existing?.validity || "")}" /></div>
              <div><label class="frm-lbl">Técnica / Aplicação</label>
                <select id="inp-pmc-technique" class="frm-inp">
                  ${PM_TECHNIQUES.map(t => `<option value="${t.code}" ${(existing?.technique || "") === t.code ? "selected" : ""}>${this.esc(t.label)}</option>`).join("")}
                </select></div>
            </div>
            <div><label class="frm-lbl">Observações</label><textarea id="inp-pmc-observations" class="frm-inp" rows="2">${this.esc(existing?.observations || "")}</textarea></div>
            <div style="max-width:220px;">
              <label class="frm-lbl">Status</label>
              <select id="inp-pmc-status" class="frm-inp">
                <option value="ACTIVE" ${existing?.status !== "INACTIVE" ? "selected" : ""}>Ativo</option>
                <option value="INACTIVE" ${existing?.status === "INACTIVE" ? "selected" : ""}>Inativo</option>
              </select>
            </div>
            <div id="pmc-form-error" style="display:none; color:var(--status-danger-solid); font-size:0.8rem;"></div>
            <div style="display:flex; justify-content:flex-end; gap:8px; margin-top:8px;">
              <button type="button" id="btn-pmc-cancel" class="btn-cancel">Cancelar</button>
              <button type="submit" class="btn-save">Salvar</button>
            </div>
          </form>
        </div>
      </div>
      <style>
        #ndt-pmc-modal .frm-lbl { font-size:0.75rem; color:var(--color-graphite-400); font-weight:600; display:block; margin-bottom:4px; }
        #ndt-pmc-modal .frm-inp { width:100%; padding:8px; background:var(--color-primary); border:1px solid var(--color-graphite-700); border-radius:6px; color: var(--color-text-primary); box-sizing:border-box; font-family:inherit; }
        #ndt-pmc-modal .btn-cancel { background:var(--color-graphite-700); color: var(--color-text-primary); border:none; padding:8px 16px; border-radius:6px; cursor:pointer; }
        #ndt-pmc-modal .btn-save { background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border:none; padding:8px 16px; border-radius:6px; font-weight:600; cursor:pointer; }
      </style>
    `;
    document.body.appendChild(modal);

    modal.querySelector('#btn-pmc-cancel')?.addEventListener('click', () => modal.remove());
    modal.querySelector('#pmc-form')?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const payload: Partial<PmConsumable> = {
        type: (modal.querySelector('#inp-pmc-type') as HTMLSelectElement).value as PmConsumable["type"],
        brand: (modal.querySelector('#inp-pmc-brand') as HTMLInputElement).value,
        product: (modal.querySelector('#inp-pmc-product') as HTMLInputElement).value,
        lot: (modal.querySelector('#inp-pmc-lot') as HTMLInputElement).value,
        manufacturingDate: (modal.querySelector('#inp-pmc-manufacturing') as HTMLInputElement).value,
        validity: (modal.querySelector('#inp-pmc-validity') as HTMLInputElement).value,
        technique: (modal.querySelector('#inp-pmc-technique') as HTMLSelectElement).value as PmConsumable["technique"],
        observations: (modal.querySelector('#inp-pmc-observations') as HTMLTextAreaElement).value,
        status: (modal.querySelector('#inp-pmc-status') as HTMLSelectElement).value as any
      };
      try {
        if (existing) {
          await this.p.pmConsumableService.update(this.currentContext, existing.id, payload);
        } else {
          await this.p.pmConsumableService.create(this.currentContext, payload);
        }
        modal.remove();
        await this.loadPmConsumables();
        this.render();
      } catch (err: any) {
        const box = modal.querySelector('#pmc-form-error') as HTMLElement;
        box.style.display = 'block';
        box.textContent = err?.safeMessage || err?.message || "Erro ao salvar o consumível.";
      }
    });
  }

  private openCatalogModal(catalogKey: NdtCatalogKey, existing: NdtCatalogItem | null) {
    const old = document.querySelector('#ndt-catalog-modal');
    if (old) old.remove();

    const label = NDT_CATALOG_SINGULAR[catalogKey];
    const modal = document.createElement('div');
    modal.id = 'ndt-catalog-modal';
    modal.innerHTML = `
      <div style="position:fixed; inset:0; background:rgba(0,0,0,0.8); display:flex; align-items:center; justify-content:center; z-index:9999; backdrop-filter:blur(4px);">
        <div style="background:var(--color-graphite-800); border:1px solid var(--color-graphite-700); border-radius:10px; padding:24px; max-width:620px; width:94%; color: var(--color-text-primary); max-height:90vh; overflow-y:auto;">
          <h3 style="margin-top:0; font-size:1.15rem; color:var(--color-text-muted);">${existing ? `Editar ${this.esc(label)}` : `Cadastrar ${this.esc(label)}`}</h3>
          <form id="cat-form" style="display:flex; flex-direction:column; gap:12px; margin-top:16px;">
            <div style="display:grid; grid-template-columns:1fr 2fr; gap:10px;">
              <div><label class="frm-lbl">Código</label><input id="inp-cat-code" class="frm-inp" value="${this.esc(existing?.code || "")}" placeholder="Opcional" /></div>
              <div><label class="frm-lbl">Nome *</label><input id="inp-cat-name" class="frm-inp" required value="${this.esc(existing?.name || "")}" /></div>
            </div>
            <div><label class="frm-lbl">Descrição</label><textarea id="inp-cat-description" class="frm-inp" rows="2">${this.esc(existing?.description || "")}</textarea></div>
            <div>
              <label class="frm-lbl">Aplicabilidade (Métodos END)</label>
              <div style="display:flex; gap:14px; flex-wrap:wrap; padding:8px 0;">
                ${NDT_METHODS.map(m => `
                  <label style="display:flex; align-items:center; gap:6px; font-size:0.82rem; color:var(--color-text-primary);">
                    <input type="checkbox" class="chk-cat-method" value="${m}" ${existing?.applicableMethods.includes(m) ? "checked" : ""} /> ${this.esc(m)}
                  </label>`).join("")}
              </div>
              <small style="color:var(--color-graphite-400); font-size:0.7rem;">Nenhum método marcado = aplicável a todos.</small>
            </div>
            <div style="max-width:220px;">
              <label class="frm-lbl">Status</label>
              <select id="inp-cat-status" class="frm-inp">
                <option value="ACTIVE" ${existing?.status !== "INACTIVE" ? "selected" : ""}>Ativo</option>
                <option value="INACTIVE" ${existing?.status === "INACTIVE" ? "selected" : ""}>Inativo</option>
              </select>
            </div>
            <div id="cat-form-error" style="display:none; color:var(--status-danger-solid); font-size:0.8rem;"></div>
            <div style="display:flex; justify-content:flex-end; gap:8px; margin-top:8px;">
              <button type="button" id="btn-cat-cancel" class="btn-cancel">Cancelar</button>
              <button type="submit" class="btn-save">Salvar</button>
            </div>
          </form>
        </div>
      </div>
      <style>
        #ndt-catalog-modal .frm-lbl { font-size:0.75rem; color:var(--color-graphite-400); font-weight:600; display:block; margin-bottom:4px; }
        #ndt-catalog-modal .frm-inp { width:100%; padding:8px; background:var(--color-primary); border:1px solid var(--color-graphite-700); border-radius:6px; color: var(--color-text-primary); box-sizing:border-box; font-family:inherit; }
        #ndt-catalog-modal .btn-cancel { background:var(--color-graphite-700); color: var(--color-text-primary); border:none; padding:8px 16px; border-radius:6px; cursor:pointer; }
        #ndt-catalog-modal .btn-save { background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border:none; padding:8px 16px; border-radius:6px; font-weight:600; cursor:pointer; }
      </style>
    `;
    document.body.appendChild(modal);

    modal.querySelector('#btn-cat-cancel')?.addEventListener('click', () => modal.remove());
    modal.querySelector('#cat-form')?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const methods = Array.from(modal.querySelectorAll('.chk-cat-method'))
        .filter(c => (c as HTMLInputElement).checked)
        .map(c => (c as HTMLInputElement).value as NdtMethod);

      const payload: Partial<NdtCatalogItem> = {
        catalogKey,
        code: (modal.querySelector('#inp-cat-code') as HTMLInputElement).value,
        name: (modal.querySelector('#inp-cat-name') as HTMLInputElement).value,
        description: (modal.querySelector('#inp-cat-description') as HTMLTextAreaElement).value,
        status: (modal.querySelector('#inp-cat-status') as HTMLSelectElement).value as any,
        applicableMethods: methods
      };

      try {
        if (existing) {
          await this.p.ndtCatalogService.update(this.currentContext, existing.id, payload);
        } else {
          await this.p.ndtCatalogService.create(this.currentContext, payload);
        }
        modal.remove();
        await this.loadCatalogItems();
        this.render();
      } catch (err: any) {
        const box = modal.querySelector('#cat-form-error') as HTMLElement;
        box.style.display = 'block';
        box.textContent = err?.safeMessage || err?.message || "Erro ao salvar o registro.";
      }
    });
  }

  /** Ativos cuja calibração os relatórios consomem: aparelho e bloco (ME/US) e o Yoke (PM). */
  private tabHasCalibrationCertificate(type: NdtAssetType): boolean {
    return type === 'ULTRASONIC_THICKNESS_GAUGE' || type === 'CALIBRATION_BLOCK' || type === 'MAGNETIC_YOKE' || type === 'PYROMETER';
  }

  private async loadCertificates(assets: NdtAsset[]) {
    const entries = await Promise.all(assets.map(async a => {
      const certs = await this.p.ndtAssetService.listCertificates(this.currentContext, a.id).catch(() => []);
      return [a.id, (certs && certs.length > 0) ? certs[0] : null] as const;
    }));
    for (const [id, cert] of entries) this.certificatesByAsset.set(id, cert);
  }

  private getTypeLabel(type: NdtAssetType) {
    switch (type) {
      case 'ULTRASONIC_THICKNESS_GAUGE': return 'Aparelho de ME-US';
      case 'ULTRASONIC_TRANSDUCER': return 'Transdutor';
      case 'MAGNETIC_YOKE': return 'Yoke Magnético';
      case 'BSCAN_EQUIPMENT': return 'Aparelho B-Scan';
      case 'CALIBRATION_BLOCK': return 'Bloco de Calibração (Escalonado de Degraus)';
      case 'PYROMETER': return 'Pirômetro';
    }
  }

  private renderHeaders(type: NdtAssetType) {
    if (type === 'ULTRASONIC_THICKNESS_GAUGE') return `<th>Código</th><th>Fabricante / Modelo</th><th>Série</th><th>Range</th><th>Certificado</th><th>Validade</th><th>Calibração</th><th>Status</th><th>Ações</th>`;
    if (type === 'ULTRASONIC_TRANSDUCER') return `<th>Código</th><th>Fabricante / Modelo</th><th>Tipo</th><th>Frequência</th><th>Dimensão</th><th>Status</th><th>Ações</th>`;
    if (type === 'MAGNETIC_YOKE') return `<th>Código</th><th>Fabricante / Modelo</th><th>Alimentação</th><th>Série</th><th>Certificado</th><th>Validade</th><th>Calibração</th><th>Status</th><th>Ações</th>`;
    if (type === 'BSCAN_EQUIPMENT') return `<th>Código</th><th>Fabricante / Modelo</th><th>Série</th><th>Software</th><th>Status</th><th>Ações</th>`;
    if (type === 'CALIBRATION_BLOCK') return `<th>Código</th><th>Identificação</th><th>Material</th><th>Qtd Degraus</th><th>Certificado</th><th>Validade</th><th>Calibração</th><th>Status</th><th>Ações</th>`;
    if (type === 'PYROMETER') return `<th>Código</th><th>Fabricante / Modelo</th><th>Série</th><th>Faixa (°C)</th><th>Emissividade</th><th>Certificado</th><th>Validade</th><th>Calibração</th><th>Status</th><th>Ações</th>`;
    return `<th>Código</th><th>Fabricante / Modelo</th><th>Status</th><th>Ações</th>`;
  }

  /** Certificado / Validade / Calibração columns, shared by the assets that carry a calibration certificate. */
  private renderCertificateCells(assetId: string): string {
    const cert = this.certificatesByAsset.get(assetId) || null;
    const status = resolveCalibrationStatus(cert?.expirationDate, new Date(), 30);
    const badge = status === 'EXPIRED' ? 'status-expired' : status === 'EXPIRING' ? 'status-expiring' : status === 'VALID' ? 'status-valid' : 'status-inactive';
    const validity = cert?.expirationDate ? new Date(cert.expirationDate).toLocaleDateString('pt-BR') : '-';
    return `
      <td>${this.esc(cert?.certificateNumber || 'NÃO INFORMADO')}</td>
      <td>${this.esc(validity)}</td>
      <td><span class="status-badge ${badge}">${status === 'EXPIRED' ? '⚠ ' : status === 'EXPIRING' ? '⏳ ' : ''}${this.esc(CALIBRATION_STATUS_LABELS[status])}</span></td>
    `;
  }

  private renderRow(a: any) {
    const td = a.technicalData || {};
    let spec = '';

    if (a.assetType === 'ULTRASONIC_THICKNESS_GAUGE') {
      spec = `<td>${td.measurementRangeMin || 0} - ${td.measurementRangeMax || 0} mm</td>${this.renderCertificateCells(a.id)}`;
    } else if (a.assetType === 'ULTRASONIC_TRANSDUCER') {
      spec = `<td>${this.esc(td.transducerType || '-')}</td><td>${this.esc(formatTransducerFrequency(td.frequencyMHz) || '-')}</td><td>${this.esc(formatTransducerDimension(td.elementDiameter, td.dimensionUnit) || '-')}</td>`;
    } else if (a.assetType === 'MAGNETIC_YOKE') {
      spec = `<td>${td.powerType || '-'}</td><td>${a.serialNumber}</td>${this.renderCertificateCells(a.id)}`;
    } else if (a.assetType === 'BSCAN_EQUIPMENT') {
      spec = `<td>${a.serialNumber}</td><td>${this.esc(td.softwareVersion || '-')}</td>`;
    } else if (a.assetType === 'PYROMETER') {
      spec = `<td>${this.esc(a.serialNumber)}</td><td>${td.measurementRangeMin ?? '-'} a ${td.measurementRangeMax ?? '-'} °C</td><td>${td.emissivity ?? '-'}</td>${this.renderCertificateCells(a.id)}`;
    } else if (a.assetType === 'CALIBRATION_BLOCK') {
      const stepCount = Array.isArray(a.steps) ? a.steps.length : 0;
      spec = `<td>${a.serialNumber}</td><td>${td.material || '-'}</td><td>${stepCount}</td>${this.renderCertificateCells(a.id)}`;
    }

    const sBadge = a.status === 'ACTIVE' ? 'status-valid' : (a.status === 'INACTIVE' ? 'status-inactive' : 'status-expired');
    const sLabel = ({ ACTIVE: 'Ativo', INACTIVE: 'Inativo', OUT_OF_SERVICE: 'Fora de serviço', CALIBRATION_DUE: 'Calibração a vencer', CALIBRATION_EXPIRED: 'Calibração vencida', UNDER_MAINTENANCE: 'Em manutenção' } as Record<string, string>)[a.status] || 'Não informado';

    return `
      <tr>
        <td style="font-weight: 600; color: var(--color-text-muted);">${a.internalCode || '-'}</td>
        <td>${a.manufacturer || '-'} / ${a.model || '-'}</td>
        ${a.assetType === 'ULTRASONIC_THICKNESS_GAUGE' ? `<td>${a.serialNumber}</td>` : ''}
        ${spec}
        <td><span class="status-badge ${sBadge}">${sLabel}</span></td>
        <td style="white-space:nowrap;">
          <button type="button" class="btn-link btn-view-asset" data-id="${a.id}" title="Visualizar detalhes">Detalhes</button>
          ${contextStore.hasPermission(PermissionCode.NdtRegistryUpdate) ? `<button type="button" class="btn-link btn-edit-asset" data-id="${a.id}">Editar</button>` : ''}
          <button type="button" class="btn-link btn-cert-history" data-id="${a.id}" title="Histórico de calibração">Histórico</button>
          <button type="button" class="btn-link btn-cert" data-id="${a.id}">+ Cert</button>
          ${a.status === 'INACTIVE' ?
            `<button type="button" class="btn-link btn-reactivate" data-id="${a.id}" style="color: var(--ia-success-fg, #4ade80);">Reativar</button>` :
            `<button type="button" class="btn-link btn-inactivate" data-id="${a.id}" style="color:var(--status-danger-solid);">Inativar</button>`
          }
          ${contextStore.hasPermission(PermissionCode.NdtRegistryDelete) ? `<button type="button" class="btn-link btn-delete-asset" data-id="${a.id}" style="color:var(--status-danger-solid);">Excluir</button>` : ''}
        </td>
      </tr>
    `;
  }

  private async openAssetDetailsModal(asset: NdtAsset) {
    const old = document.querySelector('#ndt-details-modal');
    if (old) old.remove();

    const certs: NdtAssetCertificate[] = await this.p.ndtAssetService.listCertificates(this.currentContext, asset.id).catch(() => []);
    // Uso documental real: o serviço limita a consulta ao Cliente da sessão.
    let usage: Array<{ documentType:string; reportNumber:string; testDate:string; equipmentTag:string; status:string }> = [];
    let usageError = "";
    try {
      usage = await this.p.ndtAssetService.listAssetUsage(this.currentContext, asset.id);
    } catch {
      usageError = "Não foi possível consultar o histórico de utilização neste Cliente.";
    }
    const latestCert = certs.length > 0 ? certs[0] : null;
    const certStatus = latestCert ? resolveCalibrationStatus(latestCert.expirationDate, new Date(), 30) : null;
    const badgeCert = certStatus === 'EXPIRED' ? 'status-expired' : certStatus === 'EXPIRING' ? 'status-expiring' : certStatus === 'VALID' ? 'status-valid' : 'status-inactive';
    const td: any = asset.technicalData || {};
    const assetAny: any = asset;

    const modal = document.createElement('div');
    modal.id = 'ndt-details-modal';
    modal.innerHTML = `
      <div style="position:fixed; inset:0; background:rgba(0,0,0,0.8); display:flex; align-items:center; justify-content:center; z-index:9999; backdrop-filter:blur(4px);">
        <div style="background:var(--color-graphite-800); border:1px solid var(--color-graphite-700); border-radius:10px; padding:24px; max-width:680px; width:92%; color: var(--color-text-primary); max-height:90vh; overflow-y:auto;">
          <div style="display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:16px; border-bottom:1px solid var(--color-graphite-700); padding-bottom:12px;">
            <div>
              <h3 style="margin:0 0 4px 0; font-size:1.2rem; color:var(--color-text-muted);">${this.getTypeLabel(asset.assetType)}</h3>
              <div style="font-size:0.85rem; color:var(--color-graphite-400);">Código: <strong style="color:var(--ia-fg, #FFFFFF);">${this.esc(asset.internalCode)}</strong> | Série: <strong style="color:var(--ia-fg, #FFFFFF);">${this.esc(asset.serialNumber)}</strong></div>
            </div>
            <span class="status-badge ${asset.status === 'ACTIVE' ? 'status-valid' : 'status-inactive'}">${asset.status}</span>
          </div>

          <div style="display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:16px; background:rgba(0,0,0,0.2); padding:12px; border-radius:6px;">
            <div><span style="font-size:0.75rem; color:var(--color-graphite-400);">Fabricante:</span> <div style="font-weight:600;">${this.esc(asset.manufacturer || '-')}</div></div>
            <div><span style="font-size:0.75rem; color:var(--color-graphite-400);">Modelo:</span> <div style="font-weight:600;">${this.esc(asset.model || '-')}</div></div>
            ${asset.assetType === 'ULTRASONIC_THICKNESS_GAUGE' ? `
              <div><span style="font-size:0.75rem; color:var(--color-graphite-400);">Faixa de Medição:</span> <div>${td.measurementRangeMin || 0} a ${td.measurementRangeMax || 0} mm</div></div>
              <div><span style="font-size:0.75rem; color:var(--color-graphite-400);">Resolução:</span> <div>${td.resolution || '-'}</div></div>
            ` : ''}
            ${asset.assetType === 'ULTRASONIC_TRANSDUCER' ? `
              <div><span style="font-size:0.75rem; color:var(--color-graphite-400);">Tipo de Transdutor:</span> <div>${this.esc(td.transducerType || '-')}</div></div>
              <div><span style="font-size:0.75rem; color:var(--color-graphite-400);">Frequência:</span> <div>${this.esc(formatTransducerFrequency(td.frequencyMHz) || '-')}</div></div>
              <div><span style="font-size:0.75rem; color:var(--color-graphite-400);">Dimensão:</span> <div>${this.esc(formatTransducerDimension(td.elementDiameter, td.dimensionUnit) || '-')}</div></div>
              <div><span style="font-size:0.75rem; color:var(--color-graphite-400);">Aplicação:</span> <div>${this.esc(td.applicationType || '-')}</div></div>
            ` : ''}
            ${asset.assetType === 'MAGNETIC_YOKE' ? `
              <div><span style="font-size:0.75rem; color:var(--color-graphite-400);">Tipo de Alimentação:</span> <div>${this.esc(td.powerType || '-')}</div></div>
              <div><span style="font-size:0.75rem; color:var(--color-graphite-400);">Espaçamento dos Pólos:</span> <div>${this.esc(td.poleSpacing || '-')}</div></div>
            ` : ''}
            ${asset.assetType === 'CALIBRATION_BLOCK' ? `
              <div><span style="font-size:0.75rem; color:var(--color-graphite-400);">Material:</span> <div>${this.esc(td.material || '-')}</div></div>
              <div><span style="font-size:0.75rem; color:var(--color-graphite-400);">Espessura Total:</span> <div>${td.overallThickness ? `${td.overallThickness} mm` : '-'}</div></div>
            ` : ''}
            ${asset.assetType === 'BSCAN_EQUIPMENT' ? `
              <div><span style="font-size:0.75rem; color:var(--color-graphite-400);">Versão de Software:</span> <div>${this.esc(td.softwareVersion || '-')}</div></div>
            ` : ''}
          </div>

          ${Array.isArray(assetAny.steps) && assetAny.steps.length > 0 ? `
            <div style="margin-bottom:16px;">
              <h4 style="margin:0 0 8px 0; font-size:0.85rem; color:var(--color-text-muted);">Degraus do Bloco (${assetAny.steps.length})</h4>
              <table style="width:100%; border-collapse:collapse; font-size:0.8rem;">
                <thead><tr style="background:var(--color-graphite-700); text-align:left;"><th style="padding:6px;">Degrau</th><th style="padding:6px;">Espessura Nominal</th><th style="padding:6px;">Tolerância</th></tr></thead>
                <tbody>
                  ${assetAny.steps.map((s: any, idx: number) => `<tr style="border-bottom:1px solid var(--color-graphite-700);"><td style="padding:6px;">#${s.stepNumber ?? idx + 1}</td><td style="padding:6px;">${s.nominalThickness} ${s.unit || 'mm'}</td><td style="padding:6px;">${s.tolerance ? `±${s.tolerance}` : '-'}</td></tr>`).join('')}
                </tbody>
              </table>
            </div>
          ` : ''}

          <div style="margin-bottom:16px;">
            <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
              <h4 style="margin:0; font-size:0.85rem; color:var(--color-text-muted);">Calibração Atual</h4>
              ${latestCert && certStatus ? `<span class="status-badge ${badgeCert}">${CALIBRATION_STATUS_LABELS[certStatus]}</span>` : ''}
            </div>
            ${latestCert ? `
              <div style="background:rgba(0,0,0,0.2); padding:10px; border-radius:6px; font-size:0.82rem; display:grid; grid-template-columns:1fr 1fr; gap:8px;">
                <div><strong style="color:var(--color-graphite-400);">Certificado Nº:</strong> ${this.esc(latestCert.certificateNumber || 'NÃO INFORMADO')}</div>
                <div><strong style="color:var(--color-graphite-400);">Laboratório:</strong> ${this.esc(latestCert.laboratory || '-')}</div>
                <div><strong style="color:var(--color-graphite-400);">Data de Emissão:</strong> ${latestCert.issueDate ? new Date(latestCert.issueDate).toLocaleDateString('pt-BR') : '-'}</div>
                <div><strong style="color:var(--color-graphite-400);">Data de Validade:</strong> ${latestCert.expirationDate ? new Date(latestCert.expirationDate).toLocaleDateString('pt-BR') : '-'}</div>
              </div>
            ` : `
              <div style="font-size:0.82rem; color:var(--color-graphite-400); font-style:italic;">Nenhum certificado registrado para este equipamento.</div>
            `}
          </div>

          <section aria-label="Rastreabilidade documental" style="margin:16px 0; border-top:1px solid var(--ia-border,#5C5C5C); padding-top:14px;">
            <h4 style="margin:0 0 8px; color:var(--ia-fg,#EEEEEE);">Histórico de uso em relatórios</h4>
            ${usageError ? `<p role="alert">${this.esc(usageError)}</p>`
              : usage.length ? `<div style="overflow-x:auto"><table class="data-table">
                  <thead><tr><th>Método</th><th>Relatório</th><th>Data</th><th>TAG inspecionada</th><th>Situação</th></tr></thead>
                  <tbody>${usage.map(row => `<tr>
                    <td>${this.esc(row.documentType)}</td>
                    <td>${this.esc(row.reportNumber)}</td>
                    <td>${this.esc(row.testDate)}</td>
                    <td>${this.esc(row.equipmentTag)}</td>
                    <td>${this.esc(row.status)}</td>
                  </tr>`).join("")}</tbody>
                </table></div>`
              : '<p class="ndt-muted">Nenhum uso registrado nos relatórios deste Cliente.</p>'}
          </section>
          <div style="display:flex; justify-content:flex-end; gap:8px; margin-top:20px; border-top:1px solid var(--color-graphite-700); padding-top:12px;">
            <button type="button" id="btn-details-close" class="btn-cancel" style="background:var(--color-graphite-700); color:var(--ia-fg, #FFFFFF); border:none; padding:8px 16px; border-radius:6px; cursor:pointer;">Fechar</button>
            <button type="button" id="btn-details-hist" class="btn-cancel" style="background:var(--ia-bg-raised, #383838); border:1px solid #5C5C5C; color:var(--ia-fg, #FFFFFF); padding:8px 16px; border-radius:6px; cursor:pointer;">Ver Histórico Completo</button>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(modal);

    modal.querySelector('#btn-details-close')?.addEventListener('click', () => modal.remove());
    modal.querySelector('#btn-details-hist')?.addEventListener('click', () => {
      modal.remove();
      this.openCertificateHistoryModal(asset);
    });
  }

  private async openCertificateHistoryModal(asset: NdtAsset) {
    const old = document.querySelector('#ndt-cert-history-modal');
    if (old) old.remove();

    const certs: NdtAssetCertificate[] = await this.p.ndtAssetService.listCertificates(this.currentContext, asset.id).catch(() => []);

    const modal = document.createElement('div');
    modal.id = 'ndt-cert-history-modal';
    modal.innerHTML = `
      <div style="position:fixed; inset:0; background:rgba(0,0,0,0.8); display:flex; align-items:center; justify-content:center; z-index:9999; backdrop-filter:blur(4px);">
        <div style="background:var(--color-graphite-800); border:1px solid var(--color-graphite-700); border-radius:10px; padding:24px; max-width:760px; width:94%; color: var(--color-text-primary); max-height:90vh; overflow-y:auto;">
          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:16px; border-bottom:1px solid var(--color-graphite-700); padding-bottom:12px;">
            <div>
              <h3 style="margin:0; font-size:1.15rem; color:var(--color-text-muted);">Histórico de Calibração</h3>
              <p style="margin:4px 0 0 0; font-size:0.8rem; color:var(--color-graphite-400);">${this.getTypeLabel(asset.assetType)} — ${this.esc(asset.internalCode)} (${this.esc(asset.serialNumber)})</p>
            </div>
            <button type="button" id="btn-add-cert-from-hist" style="background:var(--ia-action-bg, var(--color-primary)); color:var(--ia-action-fg, #FFFFFF); border:none; padding:6px 14px; border-radius:6px; font-size:0.8rem; font-weight:600; cursor:pointer;">+ Novo Certificado</button>
          </div>

          ${certs.length === 0 ? `
            <div style="text-align:center; padding:32px; color:var(--color-graphite-400); font-style:italic;">Nenhum certificado registrado no histórico.</div>
          ` : `
            <table style="width:100%; border-collapse:collapse; font-size:0.82rem;">
              <thead>
                <tr style="background:var(--color-graphite-700); text-align:left;">
                  <th style="padding:8px;">Nº Certificado</th>
                  <th style="padding:8px;">Laboratório</th>
                  <th style="padding:8px;">Emissão</th>
                  <th style="padding:8px;">Validade</th>
                  <th style="padding:8px;">Status</th>
                  <th style="padding:8px;">Anexo</th>
                </tr>
              </thead>
              <tbody>
                ${certs.map(c => {
                  const status = resolveCalibrationStatus(c.expirationDate, new Date(), 30);
                  const badge = status === 'EXPIRED' ? 'status-expired' : status === 'EXPIRING' ? 'status-expiring' : status === 'VALID' ? 'status-valid' : 'status-inactive';
                  const att = c.attachment || null;
                  return `
                    <tr style="border-bottom:1px solid var(--color-graphite-700);">
                      <td style="padding:8px; font-weight:600;">${this.esc(c.certificateNumber || '-')}</td>
                      <td style="padding:8px;">${this.esc(c.laboratory || '-')}</td>
                      <td style="padding:8px;">${c.issueDate ? new Date(c.issueDate).toLocaleDateString('pt-BR') : '-'}</td>
                      <td style="padding:8px;">${c.expirationDate ? new Date(c.expirationDate).toLocaleDateString('pt-BR') : '-'}</td>
                      <td style="padding:8px;"><span class="status-badge ${badge}">${this.esc(CALIBRATION_STATUS_LABELS[status])}</span></td>
                      <td style="padding:8px;">
                        ${att ? `<a href="${att.dataUrl}" download="${this.esc(att.fileName)}" style="color:var(--color-text-primary); text-decoration:underline;">📥 Anexo</a>` : '-'}
                      </td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          `}

          <div style="display:flex; justify-content:flex-end; margin-top:20px; border-top:1px solid var(--color-graphite-700); padding-top:12px;">
            <button type="button" id="btn-hist-close" style="background:var(--color-graphite-700); color:var(--ia-fg, #FFFFFF); border:none; padding:8px 18px; border-radius:6px; cursor:pointer;">Fechar</button>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(modal);

    modal.querySelector('#btn-hist-close')?.addEventListener('click', () => modal.remove());
    modal.querySelector('#btn-add-cert-from-hist')?.addEventListener('click', () => {
      modal.remove();
      this.addCertificate(asset.id);
    });
  }

  /**
   * Assistente Modal de Importação em 5 Etapas:
   * 1. Download de Modelo (XLSX com 5 abas ou CSV para aba ativa)
   * 2. Upload de Arquivo (.xlsx / .csv)
   * 3. Validação e Pré-processamento
   * 4. Staging Preview com Diffs e Status Badges
   * 5. Execução via NdtAssetService e Relatório Final
   */
  private openImportModal() {
    const old = document.querySelector('#ndt-import-modal');
    if (old) old.remove();

    let currentStep = 1;
    let selectedFile: File | null = null;
    let fileBytes: Uint8Array | null = null;
    let fileText: string | null = null;
    let stagedItems: NdtImportStagedItem[] = [];
    let importSummary: NdtImportSummary | null = null;
    let executionResult: NdtImportExecutionResult | null = null;
    let isProcessing = false;

    const modal = document.createElement('div');
    modal.id = 'ndt-import-modal';

    const updateModalView = () => {
      modal.innerHTML = `
        <div style="position:fixed; inset:0; background:rgba(0,0,0,0.85); display:flex; align-items:center; justify-content:center; z-index:9999; backdrop-filter:blur(4px);">
          <div style="background:var(--color-graphite-800); border:1px solid var(--color-graphite-700); border-radius:12px; padding:24px; max-width:880px; width:94%; color: var(--color-text-primary); max-height:92vh; display:flex; flex-direction:column;">
            <!-- Modal Header & Wizard Steps -->
            <div style="margin-bottom:18px; border-bottom:1px solid var(--color-graphite-700); padding-bottom:14px;">
              <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;">
                <h3 style="margin:0; font-size:1.25rem; color:var(--color-text-muted); display:flex; align-items:center; gap:8px;">
                  📥 Importação em Lote de Equipamentos de END
                </h3>
                <button type="button" id="btn-import-close-x" style="background:none; border:none; color:var(--color-graphite-400); font-size:1.5rem; cursor:pointer; line-height:1;">&times;</button>
              </div>
              <div style="display:flex; gap:6px; align-items:center;">
                ${[
                  { num: 1, label: "1. Modelo" },
                  { num: 2, label: "2. Upload" },
                  { num: 3, label: "3. Validação" },
                  { num: 4, label: "4. Revisão / Diffs" },
                  { num: 5, label: "5. Conclusão" }
                ].map(s => `
                  <div style="flex:1; text-align:center; padding:6px; font-size:0.75rem; font-weight:700; border-radius:4px; ${currentStep === s.num ? 'background:var(--color-primary); color:var(--ia-fg, #FFFFFF);' : (currentStep > s.num ? 'background:var(--color-graphite-700); color:var(--ia-success-fg, #4ade80);' : 'background:rgba(255,255,255,0.04); color:var(--color-graphite-400);')}">
                    ${s.label}
                  </div>
                `).join('')}
              </div>
            </div>

            <!-- Modal Body by Step -->
            <div style="flex:1; overflow-y:auto; padding:4px;">
              ${currentStep === 1 ? `
                <div>
                  <h4 style="margin:0 0 8px 0; font-size:1rem; color:var(--ia-fg, #FFFFFF);">Etapa 1: Baixar Planilhas de Modelo Padrão</h4>
                  <p style="font-size:0.85rem; color:var(--color-graphite-400); line-height:1.5; margin-bottom:18px;">
                    Baixe o modelo pré-formatado com as colunas oficiais exigidas pelo sistema. Você pode optar pela planilha completa em <strong>Excel (.xlsx)</strong> com todas as 5 abas ou pelo arquivo <strong>CSV</strong> específico da aba ativa.
                  </p>

                  <div style="display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:20px;">
                    <div style="background:rgba(255,255,255,0.03); border:1px solid var(--color-graphite-700); border-radius:8px; padding:16px; display:flex; flex-direction:column; justify-content:space-between;">
                      <div>
                        <div style="font-weight:700; font-size:0.95rem; margin-bottom:6px; color:var(--color-text-muted);">📊 Planilha Completa (.xlsx)</div>
                        <div style="font-size:0.8rem; color:var(--color-graphite-400); margin-bottom:12px;">Contém as 5 abas pré-configuradas: Aparelhos ME-US, Transdutores, Yokes, Aparelhos B-Scan e Blocos de Calibração com exemplos e certificados.</div>
                      </div>
                      <button type="button" id="btn-dl-xlsx" style="padding:9px 14px; background:var(--ia-bg-raised, #383838); border:1px solid #5C5C5C; color:var(--ia-fg, #FFFFFF); border-radius:6px; font-weight:600; font-size:0.82rem; cursor:pointer; display:flex; align-items:center; justify-content:center; gap:6px;">
                        📥 Baixar Modelo .XLSX
                      </button>
                    </div>

                    <div style="background:rgba(255,255,255,0.03); border:1px solid var(--color-graphite-700); border-radius:8px; padding:16px; display:flex; flex-direction:column; justify-content:space-between;">
                      <div>
                        <div style="font-weight:700; font-size:0.95rem; margin-bottom:6px; color:var(--color-text-muted);">📄 Modelo CSV (${this.getTypeLabel(this.activeTab as NdtAssetType)})</div>
                        <div style="font-size:0.8rem; color:var(--color-graphite-400); margin-bottom:12px;">Arquivo .csv mono-aba otimizado para o tipo de equipamento atualmente selecionado na tela.</div>
                      </div>
                      <button type="button" id="btn-dl-csv" style="padding:9px 14px; background:var(--ia-bg-raised, #383838); border:1px solid #5C5C5C; color:var(--ia-fg, #FFFFFF); border-radius:6px; font-weight:600; font-size:0.82rem; cursor:pointer; display:flex; align-items:center; justify-content:center; gap:6px;">
                        📥 Baixar Modelo .CSV
                      </button>
                    </div>
                  </div>
                </div>
              ` : ''}

              ${currentStep === 2 ? `
                <div>
                  <h4 style="margin:0 0 8px 0; font-size:1rem; color:var(--ia-fg, #FFFFFF);">Etapa 2: Selecionar Arquivo para Importação</h4>
                  <p style="font-size:0.85rem; color:var(--color-graphite-400); line-height:1.5; margin-bottom:18px;">
                    Selecione ou arraste o arquivo preenchido (.xlsx ou .csv). O sistema fará a validação estrutural completa sem alterar os dados existentes até a confirmação final.
                  </p>

                  <div id="drop-zone" style="border:2px dashed ${selectedFile ? 'var(--ia-success-fg, #4ade80)' : 'var(--color-graphite-700)'}; border-radius:10px; padding:36px 20px; text-align:center; background:rgba(0,0,0,0.2); cursor:pointer; transition:border-color 0.2s;">
                    <div style="font-size:2.4rem; margin-bottom:8px;">📁</div>
                    ${selectedFile ? `
                      <div style="font-size:0.95rem; font-weight:700; color:var(--ia-success-fg, #4ade80);">${this.esc(selectedFile.name)}</div>
                      <div style="font-size:0.8rem; color:var(--color-graphite-400); margin-top:4px;">${(selectedFile.size / 1024).toFixed(1)} KB — Clique ou arraste para trocar</div>
                    ` : `
                      <div style="font-size:0.95rem; font-weight:600; color:var(--ia-fg, #EEEEEE);">Arraste o arquivo aqui ou clique para selecionar</div>
                      <div style="font-size:0.8rem; color:var(--color-graphite-400); margin-top:6px;">Formatos suportados: .xlsx, .csv</div>
                    `}
                    <input type="file" id="inp-import-file" accept=".xlsx,.csv" style="display:none;" />
                  </div>
                </div>
              ` : ''}

              ${currentStep === 3 ? `
                <div>
                  <h4 style="margin:0 0 8px 0; font-size:1rem; color:var(--ia-fg, #FFFFFF);">Etapa 3: Validação Estrutural e Consistência</h4>
                  ${importSummary ? `
                    <div style="display:grid; grid-template-columns:repeat(auto-fit, minmax(130px, 1fr)); gap:10px; margin:16px 0;">
                      <div style="background:rgba(255,255,255,0.03); border:1px solid var(--color-graphite-700); border-radius:6px; padding:10px; text-align:center;">
                        <div style="font-size:1.3rem; font-weight:700; color:var(--ia-fg, #FFFFFF);">${importSummary.totalRows}</div>
                        <div style="font-size:0.72rem; color:var(--color-graphite-400);">Total de Linhas</div>
                      </div>
                      <div style="background:rgba(34,197,94,0.1); border:1px solid rgba(34,197,94,0.3); border-radius:6px; padding:10px; text-align:center;">
                        <div style="font-size:1.3rem; font-weight:700; color:#4ade80;">${importSummary.countNew}</div>
                        <div style="font-size:0.72rem; color:#86efac;">Novos</div>
                      </div>
                      <div style="background:rgba(59,130,246,0.1); border:1px solid rgba(59,130,246,0.3); border-radius:6px; padding:10px; text-align:center;">
                        <div style="font-size:1.3rem; font-weight:700; color:#60a5fa;">${importSummary.countUpdate}</div>
                        <div style="font-size:0.72rem; color:#93c5fd;">Atualizações</div>
                      </div>
                      <div style="background:rgba(168,85,247,0.1); border:1px solid rgba(168,85,247,0.3); border-radius:6px; padding:10px; text-align:center;">
                        <div style="font-size:1.3rem; font-weight:700; color:#c084fc;">${importSummary.countNewCalibration}</div>
                        <div style="font-size:0.72rem; color:#d8b4fe;">Novas Calibrações</div>
                      </div>
                      <div style="background:rgba(148,163,184,0.1); border:1px solid rgba(148,163,184,0.3); border-radius:6px; padding:10px; text-align:center;">
                        <div style="font-size:1.3rem; font-weight:700; color:#cbd5e1;">${importSummary.countUnchanged}</div>
                        <div style="font-size:0.72rem; color:#94a3b8;">Sem Alteração</div>
                      </div>
                      ${importSummary.totalErrors > 0 ? `
                        <div style="background:rgba(239,68,68,0.1); border:1px solid rgba(239,68,68,0.3); border-radius:6px; padding:10px; text-align:center;">
                          <div style="font-size:1.3rem; font-weight:700; color:#f87171;">${importSummary.totalErrors}</div>
                          <div style="font-size:0.72rem; color:#fca5a5;">Erros</div>
                        </div>
                      ` : ''}
                    </div>

                    ${importSummary.totalErrors > 0 ? `
                      <div style="background:rgba(239,68,68,0.08); border:1px solid rgba(239,68,68,0.3); border-radius:8px; padding:12px; margin-top:12px;">
                        <div style="font-weight:700; color:#f87171; font-size:0.85rem; margin-bottom:6px;">⚠ Problemas Identificados na Planilha:</div>
                        <ul style="margin:0; padding-left:20px; font-size:0.8rem; color:#fca5a5;">
                          ${stagedItems.filter(i => i.status === 'ERROR' || i.issues.length > 0).flatMap(i => i.issues.map(iss => `<li>Linha ${iss.rowNumber} (${iss.column || 'Geral'}): ${this.esc(iss.message)}</li>`)).slice(0, 8).join('')}
                          ${stagedItems.flatMap(i => i.issues).length > 8 ? `<li>... e outros ${stagedItems.flatMap(i => i.issues).length - 8} avisos.</li>` : ''}
                        </ul>
                      </div>
                    ` : `
                      <div style="background:rgba(34,197,94,0.08); border:1px solid rgba(34,197,94,0.3); border-radius:8px; padding:12px; margin-top:12px; color:#86efac; font-size:0.85rem;">
                        ✓ Validação concluída com sucesso! Todos os registros são compatíveis com o banco de dados.
                      </div>
                    `}
                  ` : ''}
                </div>
              ` : ''}

              ${currentStep === 4 ? `
                <div>
                  <h4 style="margin:0 0 8px 0; font-size:1rem; color:var(--ia-fg, #FFFFFF);">Etapa 4: Pré-visualização e Revisão de Alterações (Diffs)</h4>
                  <p style="font-size:0.82rem; color:var(--color-graphite-400); margin-bottom:12px;">
                    Revise cada item antes de persistir no banco de dados. Registros com erro serão ignorados.
                  </p>

                  <div style="max-height:360px; overflow-y:auto; border:1px solid var(--color-graphite-700); border-radius:6px;">
                    <table style="width:100%; border-collapse:collapse; font-size:0.78rem;">
                      <thead>
                        <tr style="background:var(--color-graphite-700); text-align:left; position:sticky; top:0;">
                          <th style="padding:7px 9px;">Linha</th>
                          <th style="padding:7px 9px;">Tipo</th>
                          <th style="padding:7px 9px;">Código / Série</th>
                          <th style="padding:7px 9px;">Fabricante / Modelo</th>
                          <th style="padding:7px 9px;">Ação Prevista</th>
                          <th style="padding:7px 9px;">Detalhes da Alteração</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${stagedItems.map(item => {
                          const badgeClass = item.status === 'NEW' ? 'status-valid' : (item.status === 'UPDATE' ? 'status-expiring' : (item.status === 'NEW_CALIBRATION' ? 'status-valid' : (item.status === 'ERROR' ? 'status-expired' : 'status-inactive')));
                          const statusLabel = item.status === 'NEW' ? 'NOVO' : (item.status === 'UPDATE' ? 'ATUALIZAÇÃO' : (item.status === 'NEW_CALIBRATION' ? 'NOVA CALIBRAÇÃO' : (item.status === 'UNCHANGED' ? 'SEM ALTERAÇÃO' : 'ERRO')));
                          return `
                            <tr style="border-bottom:1px solid var(--color-graphite-700);">
                              <td style="padding:7px 9px; font-weight:600;">#${item.rowNumber}</td>
                              <td style="padding:7px 9px;">${this.getTypeLabel(item.assetType)}</td>
                              <td style="padding:7px 9px; font-weight:600;">${this.esc(item.draft.internalCode || item.draft.serialNumber || '-')}</td>
                              <td style="padding:7px 9px;">${this.esc(item.draft.manufacturer || '-')} / ${this.esc(item.draft.model || '-')}</td>
                              <td style="padding:7px 9px;"><span class="status-badge ${badgeClass}">${statusLabel}</span></td>
                              <td style="padding:7px 9px; color:var(--color-graphite-400);">${this.esc((item.changesDescription || []).join('; ') || (item.status === 'NEW' ? 'Criação de novo equipamento' : (item.status === 'UNCHANGED' ? 'Dados idênticos aos cadastrados' : item.issues.map(i => i.message).join(' | '))))}</td>
                            </tr>
                          `;
                        }).join('')}
                      </tbody>
                    </table>
                  </div>
                </div>
              ` : ''}

              ${currentStep === 5 ? `
                <div>
                  <h4 style="margin:0 0 8px 0; font-size:1rem; color:var(--ia-fg, #FFFFFF);">Etapa 5: Conclusão da Importação</h4>
                  ${executionResult ? `
                    <div style="background:rgba(34,197,94,0.08); border:1px solid rgba(34,197,94,0.3); border-radius:8px; padding:18px; margin:16px 0; text-align:center;">
                      <div style="font-size:2.2rem; margin-bottom:8px;">🎉</div>
                      <h3 style="margin:0 0 8px 0; color:#86efac; font-size:1.2rem;">Importação Concluída com Sucesso!</h3>
                      <p style="font-size:0.85rem; color:var(--color-graphite-400); margin:0 0 16px 0;">Os dados foram gravados de forma não-destrutiva no banco de dados.</p>

                      <div style="display:flex; justify-content:center; gap:20px; flex-wrap:wrap;">
                        <div><strong style="color:#4ade80; font-size:1.2rem;">${executionResult.createdCount}</strong><div style="font-size:0.75rem; color:var(--color-graphite-400);">Equipamentos Criados</div></div>
                        <div><strong style="color:#60a5fa; font-size:1.2rem;">${executionResult.updatedCount}</strong><div style="font-size:0.75rem; color:var(--color-graphite-400);">Equipamentos Atualizados</div></div>
                        <div><strong style="color:#c084fc; font-size:1.2rem;">${executionResult.newCertificatesCount}</strong><div style="font-size:0.75rem; color:var(--color-graphite-400);">Certificados Adicionados</div></div>
                        <div><strong style="color:#94a3b8; font-size:1.2rem;">${executionResult.unchangedCount}</strong><div style="font-size:0.75rem; color:var(--color-graphite-400);">Sem Alteração</div></div>
                        ${executionResult.skippedErrorsCount > 0 ? `<div><strong style="color:#f87171; font-size:1.2rem;">${executionResult.skippedErrorsCount}</strong><div style="font-size:0.75rem; color:var(--color-graphite-400);">Erros Ignorados</div></div>` : ''}
                      </div>
                    </div>
                  ` : ''}
                </div>
              ` : ''}
            </div>

            <!-- Modal Footer Buttons -->
            <div style="display:flex; justify-content:space-between; align-items:center; margin-top:16px; border-top:1px solid var(--color-graphite-700); padding-top:14px;">
              <div>
                ${currentStep > 1 && currentStep < 5 ? `
                  <button type="button" id="btn-import-prev" style="padding:8px 16px; background:var(--color-graphite-700); color:var(--ia-fg, #FFFFFF); border:none; border-radius:6px; cursor:pointer; font-weight:600; font-size:0.82rem;">&larr; Voltar</button>
                ` : ''}
              </div>
              <div style="display:flex; gap:8px;">
                ${currentStep === 1 ? `
                  <button type="button" id="btn-import-cancel" style="padding:8px 16px; background:var(--color-graphite-700); color:var(--ia-fg, #FFFFFF); border:none; border-radius:6px; cursor:pointer;">Cancelar</button>
                  <button type="button" id="btn-import-next-1" style="padding:8px 18px; background:var(--ia-action-bg, var(--color-primary)); color:var(--ia-action-fg, #FFFFFF); border:none; border-radius:6px; cursor:pointer; font-weight:600; font-size:0.82rem;">Avançar para Upload &rarr;</button>
                ` : ''}
                ${currentStep === 2 ? `
                  <button type="button" id="btn-import-cancel" style="padding:8px 16px; background:var(--color-graphite-700); color:var(--ia-fg, #FFFFFF); border:none; border-radius:6px; cursor:pointer;">Cancelar</button>
                  <button type="button" id="btn-import-process" ${!selectedFile || isProcessing ? 'disabled style="opacity:0.5; cursor:not-allowed;"' : ''} style="padding:8px 18px; background:var(--ia-action-bg, var(--color-primary)); color:var(--ia-action-fg, #FFFFFF); border:none; border-radius:6px; cursor:pointer; font-weight:600; font-size:0.82rem;">
                    ${isProcessing ? 'Processando...' : 'Processar Arquivo &rarr;'}
                  </button>
                ` : ''}
                ${currentStep === 3 ? `
                  <button type="button" id="btn-import-cancel" style="padding:8px 16px; background:var(--color-graphite-700); color:var(--ia-fg, #FFFFFF); border:none; border-radius:6px; cursor:pointer;">Cancelar</button>
                  <button type="button" id="btn-import-to-review" ${stagedItems.every(i => i.status === 'ERROR' || i.isBlocked) ? 'disabled style="opacity:0.5; cursor:not-allowed;"' : ''} style="padding:8px 18px; background:var(--ia-action-bg, var(--color-primary)); color:var(--ia-action-fg, #FFFFFF); border:none; border-radius:6px; cursor:pointer; font-weight:600; font-size:0.82rem;">
                    Revisar Alterações &rarr;
                  </button>
                ` : ''}
                ${currentStep === 4 ? `
                  <button type="button" id="btn-import-cancel" style="padding:8px 16px; background:var(--color-graphite-700); color:var(--ia-fg, #FFFFFF); border:none; border-radius:6px; cursor:pointer;">Cancelar</button>
                  <button type="button" id="btn-import-execute" ${isProcessing ? 'disabled style="opacity:0.5; cursor:not-allowed;"' : ''} style="padding:8px 20px; background:var(--ia-success-bg, #1b5e20); color:#FFFFFF; border:none; border-radius:6px; cursor:pointer; font-weight:700; font-size:0.85rem;">
                    ${isProcessing ? 'Executando...' : '🚀 Executar Importação'}
                  </button>
                ` : ''}
                ${currentStep === 5 ? `
                  <button type="button" id="btn-import-finish" style="padding:8px 20px; background:var(--ia-action-bg, var(--color-primary)); color:var(--ia-action-fg, #FFFFFF); border:none; border-radius:6px; cursor:pointer; font-weight:700; font-size:0.85rem;">Concluir</button>
                ` : ''}
              </div>
            </div>
          </div>
        </div>
      `;

      // Event Bindings
      modal.querySelector('#btn-import-close-x')?.addEventListener('click', () => modal.remove());
      modal.querySelectorAll('#btn-import-cancel').forEach(b => b.addEventListener('click', () => modal.remove()));

      modal.querySelector('#btn-import-prev')?.addEventListener('click', () => {
        if (currentStep > 1) {
          currentStep--;
          updateModalView();
        }
      });

      // Step 1 Downloads
      modal.querySelector('#btn-dl-xlsx')?.addEventListener('click', () => {
        const bytes = buildNdtEquipmentXlsxTemplate();
        const blob = new Blob([bytes as any], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "modelo_importacao_equipamentos_end.xlsx";
        a.click();
        URL.revokeObjectURL(url);
      });

      modal.querySelector('#btn-dl-csv')?.addEventListener('click', () => {
        const targetType = (['ULTRASONIC_THICKNESS_GAUGE', 'ULTRASONIC_TRANSDUCER', 'MAGNETIC_YOKE', 'BSCAN_EQUIPMENT', 'CALIBRATION_BLOCK', 'PYROMETER'].includes(this.activeTab)) ? (this.activeTab as NdtAssetType) : 'ULTRASONIC_THICKNESS_GAUGE';
        const csvText = buildNdtEquipmentCsvTemplate(targetType);
        const blob = new Blob(["﻿" + csvText], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `modelo_importacao_${targetType.toLowerCase()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      });

      modal.querySelector('#btn-import-next-1')?.addEventListener('click', () => {
        currentStep = 2;
        updateModalView();
      });

      // Step 2 File Selection & Drag Drop
      const dropZone = modal.querySelector('#drop-zone');
      const fileInput = modal.querySelector('#inp-import-file') as HTMLInputElement | null;

      dropZone?.addEventListener('click', () => fileInput?.click());
      dropZone?.addEventListener('dragover', (e) => {
        e.preventDefault();
        (dropZone as HTMLElement).style.borderColor = 'var(--color-primary)';
      });
      dropZone?.addEventListener('dragleave', () => {
        (dropZone as HTMLElement).style.borderColor = selectedFile ? 'var(--ia-success-fg, #4ade80)' : 'var(--color-graphite-700)';
      });
      dropZone?.addEventListener('drop', (e: any) => {
        e.preventDefault();
        const files = e.dataTransfer?.files;
        if (files && files.length > 0 && files[0]) {
          handleSelectedFile(files[0]);
        }
      });

      fileInput?.addEventListener('change', () => {
        const file = fileInput.files?.[0];
        if (file) {
          handleSelectedFile(file);
        }
      });

      const handleSelectedFile = async (f: File) => {
        selectedFile = f;
        const isXlsx = f.name.toLowerCase().endsWith('.xlsx');
        if (isXlsx) {
          const buffer = await f.arrayBuffer();
          fileBytes = new Uint8Array(buffer);
          fileText = null;
        } else {
          fileText = await f.text();
          fileBytes = null;
        }
        updateModalView();
      };

      modal.querySelector('#btn-import-process')?.addEventListener('click', async () => {
        if (!selectedFile) return;
        isProcessing = true;
        updateModalView();

        try {
          let drafts: any[] = [];
          let initialIssues: any[] = [];

          if (fileBytes) {
            const parsed = await this.p.ndtEquipmentImportService.parseWorkbookBytes(fileBytes);
            drafts = parsed.drafts;
            initialIssues = parsed.issues;
          } else if (fileText) {
            const targetType = (['ULTRASONIC_THICKNESS_GAUGE', 'ULTRASONIC_TRANSDUCER', 'MAGNETIC_YOKE', 'BSCAN_EQUIPMENT', 'CALIBRATION_BLOCK', 'PYROMETER'].includes(this.activeTab)) ? (this.activeTab as NdtAssetType) : 'ULTRASONIC_THICKNESS_GAUGE';
            const parsed = this.p.ndtEquipmentImportService.parseCsvText(fileText, targetType);
            drafts = parsed.drafts;
            initialIssues = parsed.issues;
          }

          const staged = await this.p.ndtEquipmentImportService.stageImport(this.currentContext, drafts, initialIssues);
          stagedItems = staged.stagedItems;
          importSummary = staged.summary;
          currentStep = 3;
        } catch (err: any) {
          alert(err?.safeMessage || err?.message || "Erro ao processar o arquivo.");
        } finally {
          isProcessing = false;
          updateModalView();
        }
      });

      modal.querySelector('#btn-import-to-review')?.addEventListener('click', () => {
        currentStep = 4;
        updateModalView();
      });

      modal.querySelector('#btn-import-execute')?.addEventListener('click', async () => {
        isProcessing = true;
        updateModalView();

        try {
          executionResult = await this.p.ndtEquipmentImportService.executeBatchImport(this.currentContext, stagedItems);
          currentStep = 5;
          await this.loadAssets();
        } catch (err: any) {
          alert(err?.safeMessage || err?.message || "Erro durante a gravação dos dados.");
        } finally {
          isProcessing = false;
          updateModalView();
        }
      });

      modal.querySelector('#btn-import-finish')?.addEventListener('click', () => {
        modal.remove();
        this.render();
      });
    };

    updateModalView();
    document.body.appendChild(modal);
  }

  /** Aba inicial M21: composição pura isolada do formulário de cadastro. */
  private renderDashboard(): string {
    return renderNdtDashboard({
      assets: this.assets,
      certificatesByAsset: this.certificatesByAsset,
      loadError: this.assetsLoadError,
      escapeHtml: value => this.esc(value),
      typeLabel: type => this.getTypeLabel(type),
      requiresCertificate: type => this.tabHasCalibrationCertificate(type)
    });
  }

  render() {
    const isDashboard = this.activeTab === DASHBOARD_TAB;
    const isGlobal = this.isGlobalTab(this.activeTab);
    const isProcedures = this.activeTab === PROCEDURES_TAB;
    const isLpConsumables = this.activeTab === LP_CONSUMABLES_TAB;
    const isPmConsumables = this.activeTab === PM_CONSUMABLES_TAB;
    const catalogTab = isCatalogTab(this.activeTab) ? this.activeTab : null;
    const isPhysicalEquipment = !isDashboard && !isProcedures && !isLpConsumables && !isPmConsumables && !catalogTab;
    const showsCertificate = isPhysicalEquipment && this.tabHasCalibrationCertificate(this.activeTab as NdtAssetType);
    let list = !isPhysicalEquipment ? [] : this.calibrationFilter
      ? (this.assets[this.activeTab as NdtAssetType] || [])
      : (this.assetPage?.items || []);
    if (showsCertificate && this.calibrationFilter) {
      list = list.filter(a => resolveCalibrationStatus(this.certificatesByAsset.get(a.id)?.expirationDate, new Date(), 30) === this.calibrationFilter);
    }

    this.innerHTML = `
      <style>
        /* Paleta INSPECT IA: #383838 · #5C5C5C · #909090 · #AEAEAE · #EEEEEE · #FFFFFF — texto sempre mais claro que o fundo. */
        .ndt-registry-container { padding: 24px; font-family: Inter, sans-serif; color: var(--ia-fg, #EEEEEE); }
        .ndt-head { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; flex-wrap: wrap; margin-bottom: 18px; padding-bottom: 14px; border-bottom: 1px solid #5C5C5C; }
        .ndt-head h1 { margin: 0; font-size: 1.45rem; font-weight: 700; color: var(--ia-fg, #FFFFFF); letter-spacing: 0.01em; }
        .ndt-head p { margin: 6px 0 0 0; font-size: 0.85rem; color: var(--ia-fg-soft, #AEAEAE); max-width: 820px; line-height: 1.45; }
        .ndt-head .btn-register { margin-left: auto; padding: 9px 16px; background: var(--color-primary); color: var(--ia-fg, #FFFFFF); border: 1px solid #5C5C5C; border-radius: 6px; cursor: pointer; font-weight: 700; font-size: 0.84rem; white-space: nowrap; }
        .ndt-head .btn-register:hover { filter: brightness(1.15); }
        .ndt-kpis { display:grid; grid-template-columns: repeat(4,minmax(0,1fr)); gap:12px; margin:18px 0; }
        .ndt-kpi { padding:14px; border:1px solid var(--color-graphite-700,#5C5C5C); border-radius:9px; background:var(--ia-bg-raised,#383838); }
        .ndt-kpi strong { display:block; font-size:1.5rem; font-weight:750; color:var(--ia-fg,#FFFFFF); }
        .ndt-kpi span { display:block; font-size:.8rem; color:var(--ia-fg-soft,#AEAEAE); margin-top:5px; }
        .ndt-groups { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:16px; }
        .ndt-group { border:1px solid var(--color-graphite-700,#5C5C5C); background:transparent; color:var(--ia-fg-soft,#AEAEAE); border-radius:8px; padding:11px 15px; cursor:pointer; font-size:.84rem; font-weight:700; }
        .ndt-group[aria-pressed="true"] { background:var(--ia-bg-raised,#383838); border-color:var(--ia-fg-soft,#AEAEAE); color:var(--ia-fg,#FFFFFF); }
        .ndt-group:focus-visible { outline:2px solid var(--ia-fg,#EEEEEE); outline-offset:2px; }
        @media(max-width:950px){.ndt-kpis{grid-template-columns:repeat(2,minmax(0,1fr))}}
        @media(max-width:550px){.ndt-kpis{grid-template-columns:1fr}.ndt-group{width:100%;text-align:left}}

        /* Abas: quebram de linha em vez de serem cortadas; nunca texto parcial. */
        .ndt-tabs { display: flex; flex-wrap: wrap; gap: 4px 6px; margin-bottom: 20px; border-bottom: 1px solid #5C5C5C; padding-bottom: 0; }
        .ndt-tabs .tab { flex: 0 0 auto; padding: 9px 14px; cursor: pointer; color: var(--ia-fg-soft, #AEAEAE); white-space: nowrap; font-size: 0.82rem; font-weight: 600; border-bottom: 2px solid transparent; border-radius: 6px 6px 0 0; margin-bottom: -1px; }
        .ndt-tabs .tab:hover { color: var(--ia-fg, #FFFFFF); background: var(--ia-bg-raised, #383838); }
        .ndt-tabs .tab.active { color: var(--ia-fg, #FFFFFF); border-bottom-color: var(--ia-fg-soft, #AEAEAE); background: var(--ia-bg-raised, #383838); }

        /* Visão inicial de END: informação essencial, sem gráficos nem módulos paralelos. */
        .ndt-overview { display:grid; gap:14px; }
        .ndt-overview-cards { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:12px; }
        .ndt-overview-card, .ndt-overview-panel { background:var(--ia-bg-raised,#383838); border:1px solid var(--ia-border,#5C5C5C); border-radius:9px; }
        .ndt-overview-card { padding:16px 18px; border-top:2px solid var(--ia-border,#5C5C5C); }
        .ndt-overview-card span { display:block; font-size:.82rem; color:var(--ia-fg-soft,#AEAEAE); line-height:1.4; }
        .ndt-overview-card strong { display:block; margin-top:6px; font-size:1.8rem; line-height:1.2; font-variant-numeric:tabular-nums; color:var(--ia-fg,#fff); }
        .ndt-overview-card strong.good { color:var(--ia-fg,#EEEEEE); }
        .ndt-overview-card strong.warn,.ndt-overview-alert .expiring { color:var(--ia-fg,#EEEEEE); }
        .ndt-overview-card strong.bad,.ndt-overview-alert .expired { color:var(--ia-fg,#EEEEEE); }
        .ndt-overview-panel { padding:14px 18px; }
        .ndt-overview-panel h2 { font-size:.96rem; color:var(--ia-fg,#fff); margin:0 0 12px; font-weight:700; }
        .ndt-overview-alert { display:block; background:transparent; border:0; border-top:1px solid var(--ia-border,#5C5C5C); width:100%; padding:11px 2px; text-align:left; cursor:pointer; font:inherit; font-size:.84rem; color:var(--ia-fg,#fff); }
        .ndt-overview-alert:hover,.ndt-overview-alert:focus-visible { background:rgba(255,255,255,.06); }
        .ndt-overview-alert .unknown, .ndt-overview-alert .expired, .ndt-overview-alert .expiring { color:var(--ia-fg-soft,#AEAEAE); }
        .ndt-overview-link,.ndt-overview-open { border:0; background:transparent; padding:4px 0; color:var(--ia-fg,#fff); cursor:pointer; font:inherit; text-decoration:underline; text-underline-offset:3px; }
        .ndt-overview-open { font-size:.8rem; white-space:nowrap; text-decoration:none; }
        .ndt-overview-open:hover,.ndt-overview-link:hover { text-decoration:underline; }
        .ndt-overview-section-head { display:flex; gap:12px; justify-content:space-between; align-items:center; flex-wrap:wrap; }
        .ndt-muted { color:var(--ia-fg-soft,#AEAEAE); font-size:.84rem; margin:4px 0; }
        .ndt-overview-scroll { overflow-x:auto; }
        .ndt-overview-error { color:var(--ia-fg,#EEEEEE); background:var(--ia-bg-raised,#383838); font-weight:600; margin:0; padding:12px; border:1px solid var(--ia-fg-soft,#AEAEAE); border-radius:6px; }
        @media(max-width:780px) { .ndt-overview-cards { grid-template-columns:repeat(2,minmax(0,1fr)); } .ndt-registry-container { padding:14px; } .ndt-overview-panel { padding:12px; } }
        .ndt-export-btn { padding:9px 13px; border:1px solid var(--ia-border,#5C5C5C); border-radius:6px; background:var(--ia-bg-raised,#383838); color:var(--ia-fg,#EEEEEE); cursor:pointer; font-weight:600; }
        .ndt-asset-search { display:flex; max-width:600px; gap:8px; margin:0 0 14px; }
        .ndt-asset-search input { flex:1; min-width:0; padding:9px 11px; border:1px solid var(--ia-border,#5C5C5C); border-radius:6px; background:var(--ia-bg-raised,#383838); color:var(--ia-fg,#EEEEEE); }
        .ndt-asset-search button,.ndt-asset-paging button { border:1px solid var(--ia-border,#5C5C5C); background:var(--ia-bg-raised,#383838); color:var(--ia-fg,#EEEEEE); border-radius:6px; padding:8px 12px; cursor:pointer; }
        .ndt-asset-paging button:disabled { opacity:.45; cursor:default; }
        .ndt-asset-paging { display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap; margin-bottom:12px; color:var(--ia-fg-soft,#AEAEAE); font-size:.81rem; }
        .ndt-asset-paging div { display:flex; gap:6px; }
        .data-table { width: 100%; border-collapse: collapse; table-layout: auto; }
        .data-table th, .data-table td { text-align: left; padding: 11px 12px; border-bottom: 1px solid #5C5C5C; vertical-align: middle; color: var(--ia-fg, #EEEEEE); }
        .data-table th { color: var(--ia-fg, #EEEEEE); background: var(--ia-bg-raised, #383838); font-weight: 700; font-size: 0.74rem; text-transform: uppercase; letter-spacing: 0.05em; white-space: nowrap; }
        .data-table td { font-size: 0.85rem; }
        .data-table tbody tr:hover td { background: rgba(255,255,255,0.03); }
        /* Catálogos: NOME, DESCRIÇÃO e MÉTODOS recebem o espaço; AÇÕES nunca some. */
        .catalog-table th.col-code, .catalog-table td.col-code { width: 110px; white-space: nowrap; }
        .catalog-table th.col-name { width: 22%; }
        .catalog-table th.col-methods { width: 170px; }
        .catalog-table th.col-status { width: 96px; }
        .catalog-table th.col-actions, .catalog-table td.col-actions { width: 210px; white-space: nowrap; text-align: right; }
        .catalog-table td.col-desc { color: var(--ia-fg-soft, #AEAEAE); font-size: 0.82rem; }

        .status-badge { display: inline-block; padding: 4px 9px; border-radius: 4px; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.04em; white-space: nowrap; }
        .status-valid { background:#474747; color:var(--ia-fg,#EEEEEE); border:1px solid #909090; }
        .status-expiring { background:#424242; color:var(--ia-fg,#EEEEEE); border:1px solid #909090; }
        .status-expired { background:#383838; color:var(--ia-fg,#EEEEEE); border:1px solid #AEAEAE; }
        .status-inactive { background: #5C5C5C; color: var(--ia-on-color, #EEEEEE); }
        .empty-state { text-align: center; padding: 48px 16px; color: var(--ia-fg-soft, #AEAEAE); background: var(--ia-bg-raised, #383838); border: 1px dashed #5C5C5C; border-radius: 8px; }
        .proc-filters { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 18px; }
        .proc-filters .frm-lbl { font-size: 0.72rem; color: var(--ia-fg-soft, #AEAEAE); font-weight: 600; display: block; margin-bottom: 4px; }
        .proc-filters .frm-inp { width: 100%; padding: 7px 9px; background: var(--color-primary); border: 1px solid #5C5C5C; border-radius: 6px; color: var(--ia-fg, #FFFFFF); box-sizing: border-box; font-size: 0.82rem; }
        .method-badges { display: inline-flex; flex-wrap: wrap; gap: 4px; }
        .method-badge { display: inline-block; padding: 3px 9px; border-radius: 4px; font-size: 0.72rem; font-weight: 700; line-height: 1.3; background: #5C5C5C; color: var(--ia-on-color, #FFFFFF); letter-spacing: 0.03em; }
        .btn-link { display: inline-block; background: var(--ia-bg-raised, #383838); border: 1px solid #5C5C5C; border-radius: 4px; color: var(--ia-fg, #EEEEEE); cursor: pointer; font-size: 0.76rem; font-weight: 600; padding: 4px 9px; margin-left: 4px; }
        .btn-link:hover { background: #5C5C5C; color: var(--ia-on-color, #FFFFFF); text-decoration: none; }
        @media (max-width: 1100px) { .proc-filters { grid-template-columns: repeat(2, 1fr); } }
      </style>
      <div class="ndt-registry-container">
        <div class="ndt-head">
          <div>
            <h1>Gestão de Equipamentos END</h1>
            ${isDashboard ? "" : "<p>Cadastro técnico global de equipamentos, procedimentos e consumíveis de END.</p>"}
          </div>
          <div style="display: flex; gap: 10px; align-items: center; flex-wrap: wrap;">
            ${isPhysicalEquipment ? `
              <button type="button" id="btn-export-ndt-pdf" class="ndt-export-btn">Exportar PDF</button>
              <button type="button" id="btn-export-ndt-xlsx" class="ndt-export-btn">Exportar XLSX</button>
              <button id="btn-import-ndt" style="display:inline-flex; align-items:center; gap:6px; padding: 9px 16px; background: var(--ia-bg-raised, #383838); color: var(--ia-fg, #FFFFFF); border: 1px solid #5C5C5C; border-radius: 6px; cursor: pointer; font-weight: 700; font-size: 0.84rem; white-space: nowrap;">
                📥 Importar Equipamentos
              </button>
            ` : ''}
            ${isDashboard ? "" : `<button id="btn-register-ndt" class="btn-register">
              + Cadastrar ${isProcedures ? "Procedimento de END" : isLpConsumables ? "Consumível LP" : isPmConsumables ? "Consumível PM" : catalogTab ? NDT_CATALOG_SINGULAR[catalogTab] : this.getTypeLabel(this.activeTab as NdtAssetType)}
            </button>`}
          </div>
        </div>

        <nav class="ndt-groups" aria-label="Grupos do cadastro END">
          <button type="button" class="ndt-group" data-ndt-home aria-pressed="${isDashboard}">Painel Geral</button>
          <button type="button" class="ndt-group" data-group="ASSETS" aria-pressed="${!isDashboard && this.activeGroup === 'ASSETS'}">Aparelhos e Acessórios</button>
          <button type="button" class="ndt-group" data-group="DOCUMENTATION" aria-pressed="${!isDashboard && this.activeGroup === 'DOCUMENTATION'}">Documentação Técnica</button>
          <button type="button" class="ndt-group" data-group="CONSUMABLES" aria-pressed="${!isDashboard && this.activeGroup === 'CONSUMABLES'}">Consumíveis</button>
        </nav>
        <div class="ndt-tabs" role="tablist" aria-label="Tipos de cadastros" style="${isDashboard ? "display:none" : ""}">
          ${this.activeGroup === 'ASSETS' ? `
            <div class="tab ${this.activeTab === 'ULTRASONIC_THICKNESS_GAUGE' ? 'active' : ''}" data-tab="ULTRASONIC_THICKNESS_GAUGE">Aparelhos ME-US</div>
            <div class="tab ${this.activeTab === 'ULTRASONIC_TRANSDUCER' ? 'active' : ''}" data-tab="ULTRASONIC_TRANSDUCER">Transdutores</div>
            <div class="tab ${this.activeTab === 'MAGNETIC_YOKE' ? 'active' : ''}" data-tab="MAGNETIC_YOKE">Yokes Magnéticos</div>
            <div class="tab ${this.activeTab === 'BSCAN_EQUIPMENT' ? 'active' : ''}" data-tab="BSCAN_EQUIPMENT">Aparelhos B-Scan</div>
            <div class="tab ${this.activeTab === 'CALIBRATION_BLOCK' ? 'active' : ''}" data-tab="CALIBRATION_BLOCK">Blocos de Calibração</div>
            <div class="tab ${this.activeTab === 'PYROMETER' ? 'active' : ''}" data-tab="PYROMETER">Pirômetros</div>
          ` : ''}
          ${this.activeGroup === 'DOCUMENTATION' ? `
            <div class="tab ${isProcedures ? 'active' : ''}" data-tab="${PROCEDURES_TAB}">Procedimentos</div>
            ${NDT_CATALOG_KEYS.map(k => `<div class="tab ${this.activeTab === k ? 'active' : ''}" data-tab="${k}">${this.esc(NDT_CATALOG_LABELS[k])}</div>`).join('')}
          ` : ''}
          ${this.activeGroup === 'CONSUMABLES' ? `
            <div class="tab ${isLpConsumables ? 'active' : ''}" data-tab="${LP_CONSUMABLES_TAB}">Consumíveis LP</div>
            <div class="tab ${isPmConsumables ? 'active' : ''}" data-tab="${PM_CONSUMABLES_TAB}">Consumíveis PM</div>
          ` : ''}
        </div>

        ${isPhysicalEquipment ? `
          <form class="ndt-asset-search" id="ndt-asset-search" aria-label="Buscar equipamentos">
            <input name="search" type="search" autocomplete="off" value="${this.esc(this.assetSearch)}" placeholder="Buscar TAG, série, fabricante ou modelo" aria-label="Buscar equipamento">
            <button type="submit">Buscar</button>
          </form>
          ${!this.calibrationFilter ? `<div class="ndt-asset-paging">
            <span>${this.assetPageError ? this.esc(this.assetPageError) : this.assetPage ? `${this.assetPage.total} equipamento(s) encontrados` : 'Carregando equipamentos...'}</span>
            <div>
              <button type="button" id="ndt-page-prev" ${!this.assetPreviousCursors.length ? "disabled" : ""}>Anterior</button>
              <button type="button" id="ndt-page-next" ${!this.assetPage?.nextCursor ? "disabled" : ""}>Próxima</button>
            </div>
          </div>` : '<p class="ndt-muted">Filtro de calibração: exibição completa dos registros compatíveis.</p>'}
        ` : ''}
        ${showsCertificate ? `
          <div class="proc-filters" style="grid-template-columns: repeat(2, minmax(0, 260px));">
            <div>
              <label class="frm-lbl">Calibração</label>
              <select id="flt-calibration" class="frm-inp">
                <option value="" ${this.calibrationFilter === "" ? "selected" : ""}>Todas</option>
                <option value="VALID" ${this.calibrationFilter === "VALID" ? "selected" : ""}>Válida</option>
                <option value="EXPIRING" ${this.calibrationFilter === "EXPIRING" ? "selected" : ""}>A Vencer (30d)</option>
                <option value="EXPIRED" ${this.calibrationFilter === "EXPIRED" ? "selected" : ""}>Vencida</option>
                <option value="NO_EXPIRATION_DEFINED" ${this.calibrationFilter === "NO_EXPIRATION_DEFINED" ? "selected" : ""}>Não informada</option>
              </select>
            </div>
          </div>
        ` : ''}

        ${isGlobal ? `
          <form id="ndt-global-search" class="ndt-asset-search" aria-label="Pesquisa dos cadastros de END">
            <input name="search" type="search" value="${this.esc(this.globalSearch)}"
              aria-label="Pesquisar cadastros" placeholder="Pesquisar por nome, código, título, marca ou lote">
            <button type="submit">Pesquisar</button>
          </form>
          <div class="ndt-asset-paging">
            <span role="status">${this.globalError ? this.esc(this.globalError)
              : this.globalPage && this.globalPageTab === this.activeTab
                ? `${this.globalPage.total} registro(s) encontrados` : 'Carregando registros...'}</span>
            <div>
              <button type="button" id="ndt-global-prev" ${!this.globalPreviousCursors.length ? "disabled" : ""}>Anterior</button>
              <button type="button" id="ndt-global-next" ${!this.globalPage?.nextCursor ? "disabled" : ""}>Próxima</button>
            </div>
          </div>
        ` : ''}

        ${isDashboard ? this.renderDashboard() : isPmConsumables ? this.renderPmConsumablesTab() : isLpConsumables ? this.renderLpConsumablesTab() : catalogTab ? this.renderCatalogTab(catalogTab) : isProcedures ? this.renderProceduresTab() : list.length === 0 ? `
          <div class="empty-state">
            <h3 style="margin: 0 0 6px 0; color: var(--color-text-primary);">Nenhum ${this.getTypeLabel(this.activeTab as NdtAssetType)} cadastrado</h3>
          </div>
        ` : `
          <table class="data-table">
            <thead>
              <tr>${this.renderHeaders(this.activeTab as NdtAssetType)}</tr>
            </thead>
            <tbody>
              ${list.map(a => this.renderRow(a)).join('')}
            </tbody>
          </table>
        `}
      </div>
    `;

    this.querySelector('[data-ndt-home]')?.addEventListener('click', () => this.switchTab(DASHBOARD_TAB));
    this.querySelector('[data-ndt-all-assets]')?.addEventListener('click', () => {
      this.activeGroup = 'ASSETS';
      this.switchTab('ULTRASONIC_THICKNESS_GAUGE');
    });
    this.querySelectorAll<HTMLElement>('[data-dashboard-asset-id]').forEach(button => {
      button.addEventListener('click', () => {
        const type = button.dataset['goAssetType'] as NdtAssetType;
        const asset = this.assets[type]?.find(a => a.id === button.dataset['dashboardAssetId']);
        if (!asset) return;
        this.activeGroup = 'ASSETS';
        this.switchTab(type);
        this.openAssetDetailsModal(asset);
      });
    });

    // Apenas os botões com data-group são grupos. 'Painel Geral' tem seu próprio handler.
    this.querySelectorAll<HTMLButtonElement>('.ndt-group[data-group]').forEach(btn => {
      btn.addEventListener('click', () => {
        const group = btn.dataset['group'] as 'ASSETS' | 'DOCUMENTATION' | 'CONSUMABLES';
        this.activeGroup = group;
        this.calibrationFilter = '';
        this.switchTab(group === 'ASSETS' ? 'ULTRASONIC_THICKNESS_GAUGE' : group === 'DOCUMENTATION' ? PROCEDURES_TAB : LP_CONSUMABLES_TAB);
      });
    });
    this.querySelectorAll('.tab').forEach(t => {
      t.addEventListener('click', (e) => {
        this.switchTab((e.currentTarget as HTMLElement).dataset['tab'] as RegistryTab);
      });
    });

    this.querySelector('#btn-export-ndt-pdf')?.addEventListener('click', () => {
      void this.exportAssetsPdf();
    });
    this.querySelector('#btn-export-ndt-xlsx')?.addEventListener('click', () => {
      void this.exportAssetsXlsx();
    });
    this.querySelector('#btn-import-ndt')?.addEventListener('click', () => {
      this.openImportModal();
    });

    this.querySelector('#btn-register-ndt')?.addEventListener('click', () => {
      if (isPmConsumables) {
        this.openPmConsumableModal(null);
      } else if (isLpConsumables) {
        this.openLpConsumableModal(null);
      } else if (catalogTab) {
        this.openCatalogModal(catalogTab, null);
      } else if (this.activeTab === PROCEDURES_TAB) {
        this.openProcedureModal(null, false);
      } else {
        this.openRegisterModal();
      }
    });

    if (isProcedures) this.attachProcedureEvents();
    if (catalogTab) this.attachCatalogEvents(catalogTab);
    if (isLpConsumables) this.attachLpConsumableEvents();
    if (isPmConsumables) this.attachPmConsumableEvents();

    this.querySelector('#ndt-global-search')?.addEventListener('submit', e => {
      e.preventDefault();
      this.globalSearch = (this.querySelector('#ndt-global-search input') as HTMLInputElement)?.value.trim() || "";
      this.resetGlobalPage();
      this.render();
      void this.refreshGlobalPage();
    });
    this.querySelector('#ndt-global-next')?.addEventListener('click', () => {
      if (!this.globalPage?.nextCursor) return;
      this.globalPreviousCursors.push(this.globalCursor);
      this.globalCursor = this.globalPage.nextCursor;
      this.globalPage = null;
      this.render();
      void this.refreshGlobalPage();
    });
    this.querySelector('#ndt-global-prev')?.addEventListener('click', () => {
      if (!this.globalPreviousCursors.length) return;
      this.globalCursor = this.globalPreviousCursors.pop() ?? null;
      this.globalPage = null;
      this.render();
      void this.refreshGlobalPage();
    });
    this.querySelector('#ndt-asset-search')?.addEventListener('submit', e => {
      e.preventDefault();
      this.assetSearch = (this.querySelector('#ndt-asset-search input') as HTMLInputElement)?.value.trim() ?? '';
      this.assetPage = null;
      this.assetPageCursor = null;
      this.assetPreviousCursors = [];
      this.render();
      void this.refreshAssetPage();
    });
    this.querySelector('#ndt-page-next')?.addEventListener('click', () => {
      if (!this.assetPage?.nextCursor) return;
      this.assetPreviousCursors.push(this.assetPageCursor);
      this.assetPageCursor = this.assetPage.nextCursor;
      this.assetPage = null;
      this.render();
      void this.refreshAssetPage();
    });
    this.querySelector('#ndt-page-prev')?.addEventListener('click', () => {
      if (!this.assetPreviousCursors.length) return;
      this.assetPageCursor = this.assetPreviousCursors.pop() ?? null;
      this.assetPage = null;
      this.render();
      void this.refreshAssetPage();
    });
    
    this.querySelector('#flt-calibration')?.addEventListener('change', (e) => {
      this.calibrationFilter = (e.target as HTMLSelectElement).value as any;
      this.render();
    });

    this.querySelectorAll('.btn-view-asset').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).dataset['id']!;
        const asset = (this.assets[this.activeTab as NdtAssetType] || []).find(a => a.id === id);
        if (asset) this.openAssetDetailsModal(asset);
      });
    });

    this.querySelectorAll('.btn-cert-history').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).dataset['id']!;
        const asset = (this.assets[this.activeTab as NdtAssetType] || []).find(a => a.id === id);
        if (asset) this.openCertificateHistoryModal(asset);
      });
    });

    this.querySelectorAll('.btn-inactivate').forEach(btn => {
      btn.addEventListener('click', (e) => this.inactivateAsset((e.currentTarget as HTMLElement).dataset['id']!));
    });
    this.querySelectorAll('.btn-reactivate').forEach(btn => {
      btn.addEventListener('click', (e) => this.reactivateAsset((e.currentTarget as HTMLElement).dataset['id']!));
    });
    this.querySelectorAll('.btn-cert').forEach(btn => {
      btn.addEventListener('click', (e) => this.addCertificate((e.currentTarget as HTMLElement).dataset['id']!));
    });
    this.querySelectorAll('.btn-edit-asset').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).dataset['id']!;
        const asset = (this.assets[this.activeTab as NdtAssetType] || []).find(a => a.id === id);
        if (asset) this.openRegisterModal(asset);
      });
    });
    this.querySelectorAll('.btn-delete-asset').forEach(btn => {
      btn.addEventListener('click', (e) => this.deleteAsset((e.currentTarget as HTMLElement).dataset['id']!));
    });
  }
}

if (!customElements.get("app-ndt-registry-page")) {
  customElements.define("app-ndt-registry-page", NdtRegistryPage);
}



